import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Rocket, CheckCircle2, Circle, Copy, Sparkles, MessageCircle, Send,
  Calendar, Loader2, Image as ImageIcon, Heart, Smile, Home, Sparkles as Sparkle, Syringe,
  Eye, X, Zap, FileText, Layers, Film, CalendarDays, ChevronLeft, ChevronRight, Clock,
  Download, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABEL = {
  reel_seed: { label: "Reel Semilla", color: "#D97757", bg: "#FCEFE8" },
  reel_variation: { label: "Variación", color: "#A69076", bg: "#F4F0EB" },
  carousel: { label: "Carrusel", color: "#7A9180", bg: "#E6F0E8" },
  offer: { label: "Oferta", color: "#1A1C1A", bg: "#D9CDBC" },
  authority: { label: "Autoridad", color: "#5C5E5C", bg: "#EDEAE5" },
  ugc: { label: "UGC / Testimonio", color: "#A69076", bg: "#F4F0EB" },
};

const HL_ICONS = { sparkles: Sparkle, syringe: Syringe, heart: Heart, home: Home, smile: Smile };

const SECTION_TITLE_CLS = "font-display text-2xl text-[#1A1C1A]";
const TAG_CLS = "text-[10px] tracking-[0.3em] uppercase text-[#A69076] font-normal";

export default function LaunchPanel() {
  const [tab, setTab] = useState("setup"); // setup | plan | posts | carousels | calendar | dm | schedule
  const [plan, setPlan] = useState(null);
  const [dmCfg, setDmCfg] = useState({ enabled: false, keyword: "BELLA", reply_text: "" });
  const [dmStats, setDmStats] = useState({ total_sent: 0, total_failed: 0, recent: [] });
  const [metaConfigured, setMetaConfigured] = useState(false);
  const [busyDay, setBusyDay] = useState(null);
  // Days that the user clicked "Generar/Regenerar" but haven't confirmed
  // `generated:true` yet. Used to keep the "Generando…" UI alive while the
  // backend works (the POST returns instantly with just an ACK).
  const [generatingDays, setGeneratingDays] = useState(new Set());
  const [busyDM, setBusyDM] = useState(false);
  const [previewDay, setPreviewDay] = useState(null);  // day object with reel preview open
  const [editDay, setEditDay] = useState(null);  // day object being edited before (re)generation
  const [editForm, setEditForm] = useState({});
  const [lifestylePosts, setLifestylePosts] = useState([]);  // Scheduled lifestyle posts inside launch window
  const [autoSched, setAutoSched] = useState({
    start_date: new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 10),
    include_reels: true,
    include_carousels: true,
    include_posts: true,
  });
  const [autoBusy, setAutoBusy] = useState(false);

  // Music library state
  const [musicLib, setMusicLib] = useState(null);
  const [musicBusy, setMusicBusy] = useState(null);  // category id being uploaded to

  const loadPlan = async () => {
    try {
      const r = await api.get("/launch/plan");
      setPlan(r.data);
      // Merge 2 sources of lifestyle content:
      // 1) social_posts already scheduled (green — real posts)
      // 2) lifestyle_assets with recommendation but not yet scheduled (dashed — pre-suggested)
      try {
        const [rs, rl] = await Promise.all([
          api.get("/social-posts?light=true"),
          api.get("/lifestyle"),
        ]);
        const socials = Array.isArray(rs.data) ? rs.data : (rs.data?.items || []);
        const scheduled = socials
          .filter((p) => (p.kind === "lifestyle" || p.source === "lifestyle_studio") && p.scheduled_for)
          .map((p) => ({
            ...p, kind: "lifestyle",
            // rebuild image_url since light=true doesn't return image_base64
            image_url: p.image_url || `/api/public/media/social/${p.id}`,
          }));
        const assets = rl.data?.items || [];
        const preSuggested = assets
          .filter((a) => a.status !== "scheduled" && a.suggested_scheduled_for)
          .map((a) => ({
            id: a.id,
            kind: "lifestyle",
            scheduled_for: a.suggested_scheduled_for,
            image_base64: a.image_url ? null : (a.final_image_base64 || a.styled_image_base64 || a.image_base64),
            image_url: a.image_url,
            hook: a.hook || (a.analysis && a.analysis.scene) || "",
            caption: a.caption || "",
            is_suggestion: true,  // display hint
          }));
        setLifestylePosts([...scheduled, ...preSuggested]);
      } catch { /* social/lifestyle APIs may differ */ }
    }
    catch { /* noop */ }
  };
  const loadDM = async () => {
    try {
      const r = await api.get("/launch/dm-stats");
      setDmCfg(r.data.config);
      setDmStats(r.data.stats);
      setMetaConfigured(r.data.meta_configured);
    } catch (_) { /* noop */ }
  };
  const loadMusic = async () => {
    try { const r = await api.get("/music/library"); setMusicLib(r.data); }
    catch (e) { toast.error("No pude cargar la biblioteca de música"); }
  };
  const uploadMusic = async (cat, file) => {
    if (!file) return;
    setMusicBusy(cat);
    try {
      const fd = new FormData();
      fd.append("category", cat);
      fd.append("file", file);
      await api.post("/music/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Track agregado a ${cat}`);
      loadMusic();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir");
    } finally { setMusicBusy(null); }
  };
  const deleteMusic = async (filename) => {
    if (!confirm(`¿Eliminar ${filename}?`)) return;
    try {
      await api.delete(`/music/${encodeURIComponent(filename)}`);
      toast.success(`${filename} eliminado`);
      loadMusic();
    } catch (e) {
      toast.error("No pude eliminar");
    }
  };

  useEffect(() => { loadPlan(); loadDM(); loadCovers(); loadHighlightStories(); loadCoverPalettes(); }, []);
  useEffect(() => { if (tab === "music" && !musicLib) loadMusic(); }, [tab]);

  // ---------- Highlight covers + Per-highlight stories ----------
  const [covers, setCovers] = useState([]);
  const [hlStories, setHlStories] = useState({ resultados: [], tratamientos: [] });
  const [coversBusy, setCoversBusy] = useState(false);
  const [hlBusy, setHlBusy] = useState({ resultados: false, tratamientos: false });
  const [coverPalettes, setCoverPalettes] = useState({});  // { highlight_id: palette }
  const [paletteBusy, setPaletteBusy] = useState(null);

  const loadCoverPalettes = async () => {
    try {
      const r = await api.get("/launch/highlight-covers/palette");
      const map = {};
      (r.data.palettes || []).forEach((p) => { map[p.highlight_id] = p.palette; });
      setCoverPalettes(map);
    } catch (_) { /* noop */ }
  };
  const setCoverPalette = async (highlight_id, palette) => {
    setPaletteBusy(highlight_id);
    try {
      await api.post("/launch/highlight-covers/palette", { highlight_id, palette });
      setCoverPalettes((m) => ({ ...m, [highlight_id]: palette }));
      toast.success(`Portada "${highlight_id}" regenerada en ${palette === "default" ? "brand" : palette === "neon_pink" ? "neón rosa" : "neón verde"}`);
      await loadCovers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cambiando paleta");
    } finally { setPaletteBusy(null); }
  };

  const loadCovers = async () => {
    try { const r = await api.get("/launch/highlight-covers"); setCovers(r.data.covers || []); }
    catch (_) { /* noop */ }
  };
  const loadHighlightStories = async () => {
    try {
      const [r, t] = await Promise.all([
        api.get("/launch/highlight-stories/resultados"),
        api.get("/launch/highlight-stories/tratamientos"),
      ]);
      setHlStories({
        resultados: r.data.stories || [],
        tratamientos: t.data.stories || [],
      });
    } catch (_) { /* noop */ }
  };
  const genCovers = async () => {
    setCoversBusy(true);
    try {
      await api.post("/launch/highlight-covers/generate");
      toast.success("5 portadas generadas con tipografía uniforme.");
      await loadCovers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar portadas");
    } finally { setCoversBusy(false); }
  };
  const genResultados = async () => {
    setHlBusy((b) => ({ ...b, resultados: true }));
    try {
      const r = await api.post("/launch/highlight-stories/resultados/generate");
      toast.success(`${r.data.generated} antes/después generados con tus casos reales.`);
      await loadHighlightStories();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en Resultados");
    } finally { setHlBusy((b) => ({ ...b, resultados: false })); }
  };
  const genTratamientos = async () => {
    setHlBusy((b) => ({ ...b, tratamientos: true }));
    try {
      await api.post("/launch/highlight-stories/tratamientos/generate");
      toast.success("Generando 8 tratamientos hiperrealistas — espera 1-2 min.");
      await loadHighlightStories();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en Tratamientos");
    } finally { setHlBusy((b) => ({ ...b, tratamientos: false })); }
  };

  // Poll while any cover or highlight story is still queued
  useEffect(() => {
    const pending =
      covers.some((c) => c.status === "queued") ||
      hlStories.resultados.some((s) => s.status === "queued") ||
      hlStories.tratamientos.some((s) => s.status === "queued");
    if (!pending) return;
    const t = setInterval(() => { loadCovers(); loadHighlightStories(); }, 8000);
    return () => clearInterval(t);
  }, [covers, hlStories]);

  // Auto-refresh while a generation is in progress (preview modal showing 'generating'
  // OR any day in the plan is still generating). Polls /launch/plan every 12s.
  useEffect(() => {
    if (!plan) return;
    const generating =
      (previewDay?.reel?.status === "generating") ||
      plan.plan.some((d) => d.reel?.status === "generating") ||
      (plan.weekly_carousels || []).some((c) => c.status === "queued" || c.status === "generating") ||
      generatingDays.size > 0;
    if (!generating) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get("/launch/plan");
        setPlan(r.data);
        // Refresh previewDay too if it's open
        if (previewDay) {
          const fresh = r.data.plan.find((d) => d.day === previewDay.day);
          if (fresh) setPreviewDay(fresh);
        }
        // Drop days from the in-progress set once they appear as generated
        setGeneratingDays((prev) => {
          if (prev.size === 0) return prev;
          const next = new Set(prev);
          (r.data.plan || []).forEach((d) => {
            if (next.has(d.day) && d.generated) next.delete(d.day);
          });
          return next.size === prev.size ? prev : next;
        });
      } catch (_) { /* noop */ }
    }, 8000);
    return () => clearInterval(t);
  }, [plan, previewDay?.day, previewDay?.reel?.status, generatingDays]);

  // Quick lookup: how many carousels are still generating (for header pill)
  const carouselsGenerating = useMemo(() => {
    return (plan?.weekly_carousels || []).filter(
      (c) => c.status === "queued" || c.status === "generating"
    ).length;
  }, [plan?.weekly_carousels]);

  const setupChecks = useMemo(() => {
    if (!plan) return [];
    const coversReady = covers.length > 0 && covers.every((c) => c.status === "ready");
    const resultadosReady = hlStories.resultados.filter((s) => s.status === "ready").length >= 5;
    const tratamientosReady = hlStories.tratamientos.filter((s) => s.status === "ready").length >= 8;
    return [
      { id: "bio", label: "Bio optimizada con keyword 'BELLA'", done: false },
      { id: "highlights", label: "5 portadas de highlights generadas", done: coversReady },
      { id: "resultados", label: "5 antes/después en Resultados", done: resultadosReady },
      { id: "tratamientos", label: "8 tratamientos hiperrealistas", done: tratamientosReady },
      { id: "keyword", label: "Auto-respuesta DM activada", done: dmCfg.enabled },
      { id: "meta", label: "Meta (Facebook/Instagram) conectado", done: metaConfigured },
    ];
  }, [plan, dmCfg.enabled, metaConfigured, covers, hlStories]);

  const completePct = useMemo(() => {
    if (!setupChecks.length) return 0;
    const done = setupChecks.filter((c) => c.done).length;
    return Math.round((done / setupChecks.length) * 100);
  }, [setupChecks]);

  const copyText = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success("Copiado");
  };

  const openEditor = async (day) => {
    // Try restoring a draft for this day from localStorage (in case user accidentally closed)
    const draftKey = `launch-edit-draft-day-${day.day}`;
    let draft = null;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) draft = JSON.parse(raw);
    } catch (_) { draft = null; }

    const baseForm = draft || {
      custom_hook: day.hook || "",
      custom_problem: day.problem || "",
      custom_solution: day.solution || "",
      custom_cta: day.cta || "",
      extra_visual: "",
      casting: day.casting || "",
      music_category: "auto",
      full_prompt: day.reel?.sora_prompt || "",
      use_custom_prompt: false,
    };
    setEditForm(baseForm);
    setEditDay(day);
    if (draft) {
      toast("Borrador restaurado — puedes seguir donde dejaste", { duration: 3500 });
    }
    // If there's no saved prompt yet, fetch a preview so the user has a starting point
    if (!baseForm.full_prompt) {
      try {
        const r = await api.post("/launch/preview-prompt", { day: day.day });
        setEditForm((f) => ({ ...f, full_prompt: r.data.prompt }));
      } catch (_) { /* noop */ }
    }
  };

  // Persist editor draft to localStorage on every change (debounced via state)
  useEffect(() => {
    if (!editDay) return;
    try {
      localStorage.setItem(`launch-edit-draft-day-${editDay.day}`, JSON.stringify(editForm));
    } catch (_) { /* noop */ }
  }, [editDay, editForm]);

  const discardDraft = (dayNum) => {
    try { localStorage.removeItem(`launch-edit-draft-day-${dayNum}`); } catch (_) { /* noop */ }
  };

  // Refresh prompt preview whenever the structured fields change AND user hasn't manually edited
  const refreshPromptPreview = async () => {
    if (!editDay) return;
    try {
      const r = await api.post("/launch/preview-prompt", {
        day: editDay.day,
        custom_hook: editForm.custom_hook,
        custom_problem: editForm.custom_problem,
        custom_solution: editForm.custom_solution,
        custom_cta: editForm.custom_cta,
        extra_visual: editForm.extra_visual,
        casting: editForm.casting || null,
      });
      setEditForm((f) => ({ ...f, full_prompt: r.data.prompt, use_custom_prompt: false }));
      toast.success("Prompt actualizado con los nuevos valores");
    } catch (e) {
      toast.error("No se pudo actualizar el preview");
    }
  };

  const submitGenerate = async () => {
    if (!editDay) return;
    setBusyDay(editDay.day);
    try {
      const payload = {
        day: editDay.day,
        custom_hook: editForm.custom_hook,
        custom_problem: editForm.custom_problem,
        custom_solution: editForm.custom_solution,
        custom_cta: editForm.custom_cta,
        extra_visual: editForm.extra_visual,
        casting: editForm.casting || null,
        music_category: editForm.music_category,
      };
      // If user edited the full prompt, send it verbatim — bypasses prompt builder
      if (editForm.use_custom_prompt && editForm.full_prompt?.trim()) {
        payload.custom_full_prompt = editForm.full_prompt.trim();
      }
      await api.post("/launch/generate-day", payload);
      toast.success(`Día ${editDay.day}: generación iniciada. Tarda 2-5 min.`);
      discardDraft(editDay.day);
      setEditDay(null);
      setPreviewDay(null);
      loadPlan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar");
    } finally { setBusyDay(null); }
  };

  // Legacy quick-generate (no editor — kept for non-reel types like carousels)
  const generateDay = async (day) => {
    setBusyDay(day);
    setGeneratingDays((prev) => new Set(prev).add(day));
    try {
      await api.post("/launch/generate-day", { day });
      toast.success(`Día ${day}: generación iniciada. Tarda 2-5 min.`);
      loadPlan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar");
      // failed — drop from tracking
      setGeneratingDays((prev) => {
        const next = new Set(prev);
        next.delete(day);
        return next;
      });
    } finally { setBusyDay(null); }
  };

  const saveDM = async () => {
    setBusyDM(true);
    try {
      const r = await api.put("/launch/dm-config", dmCfg);
      setDmCfg(r.data);
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error("Error al guardar");
    } finally { setBusyDM(false); }
  };

  // Derive Day-1 real date by scanning multiple signals in the plan.
  // Prefer PENDING items (future dates): published ones keep old/catch-up dates
  // and would skew the labels away from the real feed schedule.
  const day1Date = useMemo(() => {
    if (!plan) return null;
    let earliestPending = null;
    let earliestAny = null;
    const cutoff = Date.now() - 12 * 3600 * 1000;
    const consider = (dt, dayNum) => {
      const anchor = new Date(dt.getTime() - (dayNum - 1) * 86400000);
      if (!earliestAny || anchor < earliestAny) earliestAny = anchor;
      if (dt.getTime() >= cutoff && (!earliestPending || anchor < earliestPending)) earliestPending = anchor;
    };
    plan.plan.forEach((d) => {
      const s = d?.reel?.scheduled_for || d?.scheduled_for;
      if (s) { try { consider(new Date(s), d.day); } catch { /* skip */ } }
    });
    (plan.daily_posts || []).forEach((p) => {
      if (p?.scheduled_for) { try { consider(new Date(p.scheduled_for), p.day); } catch { /* skip */ } }
    });
    (plan.weekly_carousels || []).forEach((c) => {
      if (c?.scheduled_for) { try { consider(new Date(c.scheduled_for), c.day); } catch { /* skip */ } }
    });
    return earliestPending || earliestAny;
  }, [plan]);

  const formatDayLabel = (dayNum) => {
    if (!day1Date) return "";
    const dt = new Date(day1Date.getTime() + (dayNum - 1) * 86400000);
    const wk = ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sa"];
    return `${wk[dt.getDay()]} ${dt.getDate()} ${dt.toLocaleString("es-MX", { month: "short" }).replace(".", "")}`;
  };

  // Merge all 4 streams by day for the master calendar (reels/posts/carousels + lifestyle from social_posts)
  const masterCalendar = useMemo(() => {
    if (!plan) return [];
    const byDay = {};
    for (let d = 1; d <= 30; d++) {
      byDay[d] = { day: d, reel: null, post: null, carousel: null, lifestyle: null };
    }
    plan.plan.forEach((d) => {
      if (byDay[d.day]) byDay[d.day].reel = d;
    });
    (plan.daily_posts || []).forEach((p) => {
      if (p && byDay[p.day]) byDay[p.day].post = p;
    });
    (plan.weekly_carousels || []).forEach((c) => {
      if (byDay[c.day]) byDay[c.day].carousel = c;
    });
    // Map lifestyle scheduled posts to their day number using day1Date memo
    if (day1Date && lifestylePosts.length > 0) {
      lifestylePosts.forEach((lp) => {
        try {
          const when = new Date(lp.scheduled_for);
          const dayNum = Math.floor((when - day1Date) / (86400 * 1000)) + 1;
          if (dayNum >= 1 && dayNum <= 30 && byDay[dayNum]) {
            byDay[dayNum].lifestyle = lp;
          }
        } catch { /* skip */ }
      });
    }
    return Object.values(byDay);
  }, [plan, lifestylePosts, day1Date]);

  const WEEKDAY_NAMES = ["L", "M", "X", "J", "V", "S", "D"];

  const testDMNow = async () => {
    setBusyDM(true);
    try {
      const r = await api.post("/launch/dm-test-run");
      toast.success(`Polling ejecutado: ${r.data.scanned} convs, ${r.data.matched} coinciden, ${r.data.sent} enviadas`);
      loadDM();
    } catch (e) {
      toast.error("Error en polling");
    } finally { setBusyDM(false); }
  };

  const [busyPost, setBusyPost] = useState(null);
  const [busyCarousel, setBusyCarousel] = useState(null);

  // Modal de preview de Post/Carrusel
  // shape: { type: 'post' | 'carousel', data: <obj>, slideIdx: number }
  const [preview, setPreview] = useState(null);
  // Inline edit state for the preview modal
  const [editingCaption, setEditingCaption] = useState(false);
  const [captionDraft, setCaptionDraft] = useState("");
  const [hashtagsDraft, setHashtagsDraft] = useState("");
  const [uploadingImg, setUploadingImg] = useState(false);
  const [savingCaption, setSavingCaption] = useState(false);
  const [fbPublishing, setFbPublishing] = useState(false);

  const openPreview = (type, data) => {
    setPreview({ type, data, slideIdx: 0 });
    setEditingCaption(false);
    setCaptionDraft(data?.caption || "");
    setHashtagsDraft((data?.hashtags || []).join(" "));
  };
  const closePreview = () => {
    setPreview(null);
    setEditingCaption(false);
  };
  const nextSlide = () => {
    setPreview((p) => {
      if (!p || p.type !== "carousel") return p;
      const total = p.data?.slide_urls?.length || 0;
      if (total <= 1) return p;
      return { ...p, slideIdx: (p.slideIdx + 1) % total };
    });
  };
  const prevSlide = () => {
    setPreview((p) => {
      if (!p || p.type !== "carousel") return p;
      const total = p.data?.slide_urls?.length || 0;
      if (total <= 1) return p;
      return { ...p, slideIdx: (p.slideIdx - 1 + total) % total };
    });
  };

  // ---------- Preview modal: edit caption / upload custom image ----------
  const _previewDbId = () => {
    const p = preview?.data || {};
    // Post uses post_id_db; carousel uses id (from social_posts doc)
    return p.post_id_db || p.id || null;
  };

  const saveCaptionEdit = async () => {
    const pid = _previewDbId();
    if (!pid) { toast.error("Este contenido aún no ha sido generado en la BD"); return; }
    setSavingCaption(true);
    try {
      const tags = hashtagsDraft
        .split(/[\s,]+/)
        .map((t) => t.replace(/^#/, "").trim())
        .filter(Boolean);
      await api.patch(`/social-posts/${pid}`, {
        caption: captionDraft,
        hashtags: tags,
      });
      toast.success("Caption y hashtags actualizados");
      // Update the modal state locally
      setPreview((p) => p && ({
        ...p,
        data: { ...p.data, caption: captionDraft, hashtags: tags },
      }));
      setEditingCaption(false);
      loadPlan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally { setSavingCaption(false); }
  };

  const uploadCustomImage = async (file) => {
    const pid = _previewDbId();
    if (!pid) { toast.error("Este contenido aún no ha sido generado en la BD"); return; }
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("El archivo debe ser una imagen"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Imagen demasiado grande (>10MB)"); return; }
    setUploadingImg(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const isCarousel = preview.type === "carousel";
      const body = isCarousel
        ? { slide_image_base64: b64, slide_index: preview.slideIdx }
        : { image_base64: b64 };
      await api.patch(`/social-posts/${pid}`, body);
      toast.success(isCarousel ? `Slide ${preview.slideIdx + 1} reemplazado` : "Imagen guardada ✓");
      // Cache-bust the preview image. Ensure image_url exists so the <img>
      // actually re-renders with the new bytes (fresh posts may not have
      // image_url set on the preview data shape).
      const bust = `?t=${Date.now()}`;
      const freshUrl = `/api/public/media/social/${pid}${bust}`;
      setPreview((p) => {
        if (!p) return p;
        const updated = { ...p.data };
        if (isCarousel && Array.isArray(updated.slide_urls) && updated.slide_urls.length) {
          updated.slide_urls = updated.slide_urls.map((u, i) => {
            if (i !== p.slideIdx) return u;
            const base = (u || "").split("?")[0] || `/api/public/media/social/${pid}/slide/${i}`;
            return base + bust;
          });
        } else if (isCarousel) {
          // Carousel without slide_urls — inject just the current slide URL.
          updated.slide_urls = [`/api/public/media/social/${pid}/slide/${p.slideIdx}${bust}`];
        } else {
          updated.image_url = ((updated.image_url || `/api/public/media/social/${pid}`).split("?")[0]) + bust;
          updated.has_image = true;
        }
        return { ...p, data: updated };
      });
      loadPlan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo imagen");
    } finally { setUploadingImg(false); }
  };

  const publishNowToFacebook = async () => {
    const pid = _previewDbId();
    if (!pid) { toast.error("Este contenido aún no ha sido generado"); return; }
    if (!confirm("¿Publicar esta pieza AHORA en la página de Facebook?\n\nSe subirá la imagen + caption directamente al Feed de la Página.")) return;
    setFbPublishing(true);
    try {
      const r = await api.post(`/meta/publish-now-fb/social/${pid}`);
      const fbId = r?.data?.result?.facebook_id || "OK";
      toast.success(`Publicado en Facebook (id: ${fbId})`);
      loadPlan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error publicando en FB");
    } finally { setFbPublishing(false); }
  };

  const generatePostPreview = async (day) => {
    setBusyPost(day);
    try {
      await api.post("/launch/generate-post-preview", { day });
      toast.success(`Post Día ${day}: generando imagen y caption...`);
      // poll for completion
      setTimeout(() => loadPlan(), 8000);
      setTimeout(() => loadPlan(), 20000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusyPost(null); }
  };

  const generateCarouselPreview = async (carousel_id) => {
    setBusyCarousel(carousel_id);
    try {
      await api.post("/launch/generate-carousel-preview", { carousel_id });
      toast.success("Carrusel generando — tarda 30-60s");
      setTimeout(() => loadPlan(), 4000);
      setTimeout(() => loadPlan(), 15000);
      setTimeout(() => loadPlan(), 35000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusyCarousel(null); }
  };

  const triggerAutoSchedule = async () => {
    if (!autoSched.start_date) { toast.error("Selecciona fecha de inicio"); return; }
    if (!confirm(`¿Programar 30 días de contenido automáticamente?\n\nSe generarán ~60 piezas (Reels + Posts + Carruseles) escalonadamente en las próximas horas y se publicarán en sus fechas asignadas.\n\nEsto consume cuota de Sora y Gemini.`)) return;
    setAutoBusy(true);
    try {
      const r = await api.post("/launch/auto-schedule", {
        ...autoSched,
        timezone_offset_hours: -6,
      });
      toast.success(r.data.message, { duration: 8000 });
      loadPlan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en auto-programación");
    } finally { setAutoBusy(false); }
  };

  const [reschedBusy, setReschedBusy] = useState(false);
  const triggerReschedule = async () => {
    if (!autoSched.start_date) { toast.error("Selecciona fecha de inicio"); return; }
    if (!confirm(`Re-programar el contenido YA generado para que Día 1 = ${autoSched.start_date}?\n\nNo se generará nada nuevo — sólo se actualizan las fechas de publicación.`)) return;
    setReschedBusy(true);
    try {
      const r = await api.post("/launch/reschedule", {
        start_date: autoSched.start_date,
        timezone_offset_hours: -6,
      });
      toast.success(r.data.message, { duration: 8000 });
      loadPlan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error re-programando");
    } finally { setReschedBusy(false); }
  };

  const [publisherBusy, setPublisherBusy] = useState(false);
  const triggerPublisherNow = async () => {
    if (!confirm("¿Ejecutar el auto-publisher AHORA?\n\nPublicará todas las piezas con fecha vencida directamente en Instagram + Facebook. Útil si una publicación programada no salió a tiempo.")) return;
    setPublisherBusy(true);
    try {
      const r = await api.post("/launch/run-publisher-now");
      const { processed_reels: pr, processed_posts: pp } = r.data;
      if (pr + pp === 0) {
        toast.info("Nada pendiente. Todo está al día.");
      } else {
        toast.success(`Publicado: ${pr} reels + ${pp} posts`, { duration: 8000 });
      }
      loadPlan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error ejecutando publisher");
    } finally { setPublisherBusy(false); }
  };

  if (!plan) return <div className="text-[#5C5E5C]">Cargando…</div>;

  return (
    <div className="space-y-8" data-testid="launch-panel">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1A1C1A] to-[#2a2b29] text-[#F4F0EB] rounded-2xl p-8 lg:p-10">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A69076] font-normal mb-3">— Plan estratégico</div>
            <h2 className="font-display text-4xl lg:text-5xl mb-3">Lanzamiento de 30 días</h2>
            <p className="text-sm text-[#D9CDBC] max-w-lg leading-relaxed">
              De 0 seguidores a una cuenta que vende. 10 Reels semilla con framework PAS,
              auto-respuesta DM gratis, y pillar de autoridad semanal.
            </p>
          </div>
          <div className="flex items-end gap-4">
            <div className="text-right">
              <div className="font-display text-5xl text-[#F4F0EB]">{completePct}%</div>
              <div className={TAG_CLS}>Setup completo</div>
            </div>
          </div>
        </div>
        <div className="flex gap-1 mt-8 border-b border-[#3a3b39] overflow-x-auto">
          {[
            { id: "setup", label: "Setup", icon: CheckCircle2 },
            { id: "calendar", label: "Cronograma", icon: CalendarDays },
            { id: "plan", label: "Reels (30)", icon: Calendar },
            { id: "posts", label: "Posts (30)", icon: FileText },
            { id: "carousels", label: "Carruseles (8)", icon: Layers },
            { id: "schedule", label: "Auto-programar", icon: Zap },
            { id: "music", label: "Música", icon: Film },
            { id: "dm", label: "Auto-DM «BELLA»", icon: MessageCircle },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`launch-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-normal transition-all flex items-center gap-2 whitespace-nowrap ${
                  tab === t.id
                    ? "text-[#F4F0EB] border-b-2 border-[#D97757] -mb-px"
                    : "text-[#A69076] hover:text-[#D9CDBC]"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab: SETUP */}
      {tab === "setup" && (
        <div className="space-y-6">
          {/* Bio */}
          <div className="bg-white rounded-2xl p-7 border border-[#E6E2DC]">
            <div className={TAG_CLS}>1 · BIO</div>
            <h3 className={SECTION_TITLE_CLS + " mt-2 mb-4"}>Bio optimizada</h3>
            <p className="text-xs text-[#5C5E5C] mb-4 leading-relaxed">
              Copia esta bio en tu perfil de Instagram. Sigue una estructura probada:
              <strong> servicio + beneficio + call-to-action con keyword</strong>.
            </p>
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal">Nombre del perfil</Label>
                <div className="bg-[#F4F0EB] rounded-lg p-3 mt-1 font-mono text-sm flex items-center justify-between">
                  <span>{plan.setup.bio.name_options[0]}</span>
                  <Button variant="ghost" size="sm" onClick={() => copyText(plan.setup.bio.name_options[0])}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal">Bio</Label>
                <div className="bg-[#F4F0EB] rounded-lg p-3 mt-1 font-mono text-sm whitespace-pre-line flex items-start justify-between gap-3">
                  <span>{plan.setup.bio.bio}</span>
                  <Button variant="ghost" size="sm" onClick={() => copyText(plan.setup.bio.bio)}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal">Link (botón &quot;Reservar&quot;)</Label>
                <div className="bg-[#F4F0EB] rounded-lg p-3 mt-1 font-mono text-xs flex items-center justify-between gap-3">
                  <span className="truncate">{plan.setup.bio.link}</span>
                  <Button variant="ghost" size="sm" onClick={() => copyText(plan.setup.bio.link)}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Highlights — covers generator + download */}
          <div className="bg-white rounded-2xl p-7 border border-[#E6E2DC]">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <div className={TAG_CLS}>2 · HIGHLIGHTS</div>
                <h3 className={SECTION_TITLE_CLS + " mt-2"}>5 historias destacadas</h3>
                <p className="text-xs text-[#5C5E5C] mt-1 leading-relaxed max-w-md">
                  Genera las 5 portadas tipográficas y descárgalas para subirlas a Instagram.
                </p>
              </div>
              <Button
                onClick={genCovers}
                disabled={coversBusy || covers.some((c) => c.status === "queued")}
                className="rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white text-xs uppercase tracking-[0.2em] h-10 px-5 gap-2"
                data-testid="launch-gen-covers"
              >
                {coversBusy || covers.some((c) => c.status === "queued")
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Sparkles className="w-4 h-4" />}
                {covers.some((c) => c.status === "ready") ? "Regenerar portadas" : "Generar portadas"}
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="launch-covers-grid">
              {(covers.length ? covers : launch_setup_highlight_placeholders(plan)).map((c) => (
                <HighlightCoverCard
                  key={c.highlight_id}
                  cover={c}
                  currentPalette={coverPalettes[c.highlight_id] || c.palette || "default"}
                  onPaletteChange={(pal) => setCoverPalette(c.highlight_id, pal)}
                  busy={paletteBusy === c.highlight_id}
                />
              ))}
            </div>
          </div>

          {/* Resultados — 5 antes/después */}
          <div className="bg-white rounded-2xl p-7 border border-[#E6E2DC]">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <div className={TAG_CLS}>RESULTADOS · 5 ANTES/DESPUÉS</div>
                <h3 className={SECTION_TITLE_CLS + " mt-2"}>Historias con casos reales</h3>
                <p className="text-xs text-[#5C5E5C] mt-1 leading-relaxed max-w-md">
                  Usa tus mejores casos de la galería. Caption + hashtags generados con IA.
                </p>
              </div>
              <Button
                onClick={genResultados}
                disabled={hlBusy.resultados}
                className="rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white text-xs uppercase tracking-[0.2em] h-10 px-5 gap-2"
                data-testid="launch-gen-resultados"
              >
                {hlBusy.resultados ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {hlStories.resultados.some((s) => s.status === "ready") ? "Regenerar 5" : "Generar 5 antes/después"}
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="launch-resultados-grid">
              {hlStories.resultados.map((s) => (
                <HighlightStoryCard key={s.id} story={s} onCopy={copyText} kind="resultado" />
              ))}
              {hlStories.resultados.length === 0 && (
                <div className="col-span-full text-center text-xs text-[#5C5E5C] py-8 border border-dashed border-[#E6E2DC] rounded-xl">
                  Aún no has generado las 5 historias de antes/después. Toca <span className="font-semibold">Generar 5 antes/después</span>.
                </div>
              )}
            </div>
          </div>

          {/* Tratamientos — 8 hiperrealistas */}
          <div className="bg-white rounded-2xl p-7 border border-[#E6E2DC]">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <div className={TAG_CLS}>TRATAMIENTOS · 8 HIPERREALISTAS</div>
                <h3 className={SECTION_TITLE_CLS + " mt-2"}>Catálogo de tratamientos</h3>
                <p className="text-xs text-[#5C5E5C] mt-1 leading-relaxed max-w-md">
                  Una historia por tratamiento, con foto hiperrealista, beneficio y CTA.
                </p>
              </div>
              <Button
                onClick={genTratamientos}
                disabled={hlBusy.tratamientos || hlStories.tratamientos.some((s) => s.status === "queued")}
                className="rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white text-xs uppercase tracking-[0.2em] h-10 px-5 gap-2"
                data-testid="launch-gen-tratamientos"
              >
                {(hlBusy.tratamientos || hlStories.tratamientos.some((s) => s.status === "queued"))
                  ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {hlStories.tratamientos.some((s) => s.status === "ready") ? "Regenerar 8" : "Generar 8 tratamientos"}
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="launch-tratamientos-grid">
              {hlStories.tratamientos.map((s) => (
                <HighlightStoryCard key={s.id} story={s} onCopy={copyText} kind="tratamiento" />
              ))}
              {hlStories.tratamientos.length === 0 && (
                <div className="col-span-full text-center text-xs text-[#5C5E5C] py-8 border border-dashed border-[#E6E2DC] rounded-xl">
                  Aún no has generado los 8 tratamientos. Toca <span className="font-semibold">Generar 8 tratamientos</span>.
                </div>
              )}
            </div>
          </div>

          {/* Authority pillar reminder */}
          <div className="bg-[#1A1C1A] text-[#F4F0EB] rounded-2xl p-7">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A69076] font-normal">3 · AUTORIDAD</div>
            <h3 className="font-display text-2xl mt-2 mb-3">{plan.setup.authority_pillar.label}</h3>
            <p className="text-sm text-[#D9CDBC] leading-relaxed">{plan.setup.authority_pillar.description}</p>
          </div>

          {/* Checklist progress */}
          <div className="bg-white rounded-2xl p-7 border border-[#E6E2DC]">
            <div className={TAG_CLS}>CHECKLIST</div>
            <h3 className={SECTION_TITLE_CLS + " mt-2 mb-5"}>Setup completo</h3>
            <div className="space-y-3">
              {setupChecks.map((c) => (
                <div key={c.id} className="flex items-center gap-3 py-2">
                  {c.done ? (
                    <CheckCircle2 className="w-5 h-5 text-[#7A9180]" />
                  ) : (
                    <Circle className="w-5 h-5 text-[#A69076]" />
                  )}
                  <span className={`text-sm ${c.done ? "text-[#1A1C1A]" : "text-[#5C5E5C]"}`}>{c.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab: PLAN */}
      {tab === "plan" && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl p-6 border border-[#E6E2DC]">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 text-center">
              <div>
                <div className="font-display text-2xl text-[#1A1C1A]">{plan.summary.total_days}</div>
                <div className={TAG_CLS}>días</div>
              </div>
              {Object.entries(plan.summary.breakdown).map(([k, v]) => (
                <div key={k}>
                  <div className="font-display text-2xl text-[#1A1C1A]">{v}</div>
                  <div className={TAG_CLS}>{TYPE_LABEL[k]?.label || k}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {plan.plan.map((d) => {
              const typeInfo = TYPE_LABEL[d.type] || { label: d.type, color: "#5C5E5C", bg: "#F4F0EB" };
              const isManual = d.manual === true;
              const isGenerating = generatingDays.has(d.day) || d.reel?.status === "generating";
              return (
                <div
                  key={d.day}
                  data-testid={`launch-day-${d.day}`}
                  className={`bg-white rounded-xl border p-5 flex items-start gap-5 transition-all ${
                    isGenerating
                      ? "border-[#A69076] ring-2 ring-[#A69076]/30 shadow-md"
                      : d.generated ? "border-[#7A9180] bg-[#FAFCFA]" : "border-[#E6E2DC] hover:border-[#A69076]"
                  }`}
                >
                  <div className="flex-shrink-0 text-center w-14">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#A69076]">Día</div>
                    <div className="font-display text-3xl text-[#1A1C1A] leading-none mt-1">{d.day}</div>
                    <div className="text-[10px] text-[#5C5E5C] mt-1">{d.scheduled_time}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span
                        className="text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-full font-medium"
                        style={{ color: typeInfo.color, background: typeInfo.bg }}
                      >
                        {typeInfo.label}
                      </span>
                      {d.generated && (
                        <span className="text-[10px] tracking-[0.2em] uppercase text-[#7A9180] flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Generado
                        </span>
                      )}
                      {isGenerating && (
                        <span className="text-[10px] tracking-[0.2em] uppercase text-[#A69076] flex items-center gap-1.5" data-testid={`day-generating-${d.day}`}>
                          <Loader2 className="w-3 h-3 animate-spin" /> Generando · 2-5 min
                        </span>
                      )}
                    </div>
                    <div className="font-display text-lg text-[#1A1C1A] leading-snug">
                      {d.hook || d.topic}
                    </div>
                    {d.problem && (
                      <div className="text-xs text-[#5C5E5C] mt-2 leading-relaxed">
                        <strong>Problema:</strong> {d.problem}
                      </div>
                    )}
                    {d.solution && (
                      <div className="text-xs text-[#5C5E5C] mt-1 leading-relaxed">
                        <strong>Solución:</strong> {d.solution}
                      </div>
                    )}
                    {d.cta && (
                      <div className="text-xs text-[#D97757] mt-2 italic">{d.cta}</div>
                    )}
                    {isGenerating && (
                      <div className="mt-3 h-1.5 bg-[#EFEAE2] rounded-full overflow-hidden">
                        <div className="h-full bg-[#A69076] animate-pulse" style={{ width: "55%" }} />
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 flex flex-col gap-2">
                    {isManual ? (
                      <div className="text-[10px] text-[#A69076] italic max-w-[120px] text-right">
                        Requiere paciente real
                      </div>
                    ) : (
                      <>
                        {d.generated && d.reel && (
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => setPreviewDay(d)}
                            data-testid={`launch-view-day-${d.day}`}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1.5" /> Ver
                          </Button>
                        )}
                        {d.generated && d.type === "carousel" && d.social_post && (
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => openPreview("carousel", {
                              ...d,
                              ...d.social_post,
                              carousel_id: `day_${d.day}`,
                              week: Math.ceil(d.day / 7),
                            })}
                            data-testid={`launch-view-carousel-${d.day}`}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1.5" /> Ver {d.social_post.slide_count || ""} slides
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant={d.generated ? "outline" : "default"}
                          onClick={() => {
                            // Carousel uses simple generate (no Sora prompt to edit)
                            if (d.type === "carousel") return generateDay(d.day);
                            openEditor(d);
                          }}
                          disabled={busyDay === d.day || isGenerating}
                          data-testid={`launch-generate-day-${d.day}`}
                        >
                          {busyDay === d.day || isGenerating ? (
                            <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Generando…</>
                          ) : d.generated ? (
                            "Regenerar"
                          ) : (
                            <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generar</>
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tab: CALENDAR (master 30-day grid) */}
      {tab === "calendar" && (
        <div className="space-y-5">
          {/* Manual publisher trigger */}
          <div className="flex justify-end">
            <Button
              onClick={triggerPublisherNow}
              disabled={publisherBusy}
              variant="outline"
              className="border-[#D97757] text-[#D97757] hover:bg-[#D97757]/10 gap-2"
              data-testid="launch-run-publisher-now"
            >
              {publisherBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Publicando…</> : <><Zap className="w-4 h-4" /> Ejecutar publisher ahora</>}
            </Button>
          </div>

          {/* Próximas 24h — quick preview */}
          <UpcomingCard onOpenModal={openPreview} />

          <div className="bg-white rounded-2xl p-6 border border-[#E6E2DC]">
            <div className={TAG_CLS}>CRONOGRAMA MAESTRO · 30 DÍAS · 68 PIEZAS</div>
            <h3 className={SECTION_TITLE_CLS + " mt-2 mb-3"}>Vista de calendario completa</h3>
            <div className="flex flex-wrap gap-4 text-xs text-[#5C5E5C] mt-3">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#D97757]" /> Reel (12:00)
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#7A9180]" /> Post (18:00)
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#1A1C1A]" /> Carrusel (20:00)
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#E53E3E]" /> Falta en Facebook
              </span>
            </div>
          </div>

          {/* Weekday header */}
          <div className="grid grid-cols-7 gap-2 px-1">
            {WEEKDAY_NAMES.map((w, i) => (
              <div key={i} className="text-center text-[10px] tracking-[0.3em] uppercase text-[#A69076] font-normal">
                {w}
              </div>
            ))}
          </div>

          {/* 30-day grid */}
          <div className="grid grid-cols-7 gap-2">
            {masterCalendar.map((d) => {
              const isMonday = (d.day - 1) % 7 === 0;
              const isToday = false;
              const slots = [];
              if (d.reel) slots.push({ kind: "reel", data: d.reel });
              if (d.post) slots.push({ kind: "post", data: d.post });
              if (d.carousel) slots.push({ kind: "carousel", data: d.carousel });
              if (d.lifestyle) slots.push({ kind: "lifestyle", data: d.lifestyle });
              return (
                <div
                  key={d.day}
                  className={`bg-white rounded-xl border p-2.5 min-h-[140px] flex flex-col gap-1.5 transition-all hover:border-[#A69076] ${
                    isToday ? "border-[#D97757] ring-2 ring-[#D97757]/30" : "border-[#E6E2DC]"
                  }`}
                  data-testid={`calendar-day-${d.day}`}
                >
                  <div className="flex items-baseline justify-between gap-1">
                    <div className="flex items-baseline gap-1.5 min-w-0">
                      <div className="font-display text-lg text-[#1A1C1A] leading-none flex-shrink-0">{d.day}</div>
                      {day1Date && (
                        <div className="text-[9px] tracking-widest uppercase text-[#5C5E5C] truncate">
                          {formatDayLabel(d.day)}
                        </div>
                      )}
                    </div>
                    {isMonday && (
                      <span className="text-[8px] tracking-[0.2em] uppercase text-[#A69076] flex-shrink-0">S{Math.ceil(d.day / 7)}</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    {slots.map((s, i) => (
                      <CalendarSlotThumb
                        key={`${d.day}-${s.kind}-${i}`}
                        slot={s}
                        onOpen={() => {
                          if (s.kind === "reel") {
                            s.data.generated && s.data.reel ? setPreviewDay(s.data) : openEditor(s.data);
                          } else if (s.kind === "post") {
                            openPreview("post", s.data);
                          } else if (s.kind === "carousel") {
                            openPreview("carousel", s.data);
                          } else if (s.kind === "lifestyle") {
                            // Lifestyle uses the same modal as post — shape data with expected keys
                            const ls = s.data || {};
                            const pid = ls.id || ls.post_id_db || ls.post_id;
                            let hh = "17:00";
                            try {
                              if (ls.scheduled_for) {
                                const dt = new Date(ls.scheduled_for);
                                hh = `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
                              }
                            } catch { /* keep default */ }
                            openPreview("post", {
                              ...ls,
                              post_id_db: pid,
                              image_url: ls.image_url || (pid ? `/api/public/media/social/${pid}` : null),
                              type: "Lifestyle",
                              day: d.day,
                              scheduled_time: hh,
                              service: ls.service || "personal",
                              topic: ls.topic || ls.hook || (ls.caption || "").split("\n")[0] || "Post lifestyle",
                              _is_lifestyle: true,
                            });
                          }
                        }}
                        dataTestId={`cal-${s.kind}-${d.day}`}
                      />
                    ))}
                    {slots.length === 0 && (
                      <div className="text-[9px] text-[#B0A99E] italic pt-2">Sin contenido</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Total piece breakdown */}
          <div className="bg-[#F4F0EB] rounded-xl p-5 grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="font-display text-3xl text-[#D97757]">{plan.plan.filter(d => d.type !== "ugc" && d.type !== "carousel").length}</div>
              <div className={TAG_CLS}>Reels totales</div>
            </div>
            <div>
              <div className="font-display text-3xl text-[#1A1C1A]">{plan.weekly_carousels?.length || 0}</div>
              <div className={TAG_CLS}>Carruseles</div>
            </div>
            <div>
              <div className="font-display text-3xl text-[#7A9180]">{plan.daily_posts?.length || 0}</div>
              <div className={TAG_CLS}>Posts diarios</div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: POSTS */}
      {tab === "posts" && plan.daily_posts && (
        <div className="space-y-3">
          <div className="bg-white rounded-2xl p-6 border border-[#E6E2DC]">
            <div className={TAG_CLS}>30 POSTS DIARIOS · FEED ESTÁTICO</div>
            <h3 className={SECTION_TITLE_CLS + " mt-2"}>1 post diario (18:00)</h3>
            <p className="text-xs text-[#5C5E5C] mt-2 leading-relaxed max-w-2xl">
              Rotación: quotes inspiracionales, mitos rotos, tips accionables, datos impactantes,
              productos destacados, antes/después y testimonios. Diseñados para SAVE y SHARE.
            </p>
          </div>
          {plan.daily_posts.map((p) => (
            <div key={p.day} className="bg-white rounded-xl border border-[#E6E2DC] p-5 flex items-start gap-5">
              <div className="flex-shrink-0 text-center w-14">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#A69076]">Día</div>
                <div className="font-display text-3xl text-[#1A1C1A] leading-none mt-1">{p.day}</div>
                <div className="text-[10px] text-[#5C5E5C] mt-1">{p.scheduled_time}</div>
              </div>
              {/* Image thumbnail */}
              <button
                type="button"
                onClick={() => p.has_image && p.image_url && openPreview("post", p)}
                disabled={!p.has_image || !p.image_url}
                className={`flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-[#F4F0EB] border border-[#E6E2DC] flex items-center justify-center transition ${p.has_image && p.image_url ? "cursor-zoom-in hover:ring-2 hover:ring-[#D97757]/40 hover:border-[#D97757]/60" : "cursor-default"}`}
                data-testid={`post-thumb-btn-${p.day}`}
                title={p.has_image ? "Ver imagen en grande" : ""}
              >
                {p.has_image && p.image_url ? (
                  <img
                    src={`${BACKEND}${p.image_url}`}
                    alt={p.topic}
                    className="w-full h-full object-cover"
                    data-testid={`post-img-${p.day}`}
                  />
                ) : p.generated && p.status === "queued" ? (
                  <Loader2 className="w-5 h-5 animate-spin text-[#A69076]" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-[#D9CDBC]" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <span
                  className="text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-full font-medium inline-block mb-2"
                  style={{ color: "#5C5E5C", background: "#F4F0EB" }}
                >
                  {p.type}
                </span>
                <div className="text-sm text-[#1A1C1A] leading-relaxed">{p.topic}</div>
                <div className="text-[10px] text-[#A69076] mt-1 capitalize">Servicio: {p.service}</div>
                {p.caption && (
                  <div className="text-[10px] text-[#5C5E5C] mt-2 line-clamp-2 italic leading-relaxed">
                    {p.caption.slice(0, 160)}…
                  </div>
                )}
              </div>
              <div className="flex-shrink-0">
                <Button
                  size="sm"
                  variant={p.generated ? "outline" : "default"}
                  onClick={() => generatePostPreview(p.day)}
                  disabled={busyPost === p.day}
                  data-testid={`gen-post-${p.day}`}
                >
                  {busyPost === p.day ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : p.generated ? (
                    "Regenerar"
                  ) : (
                    <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generar</>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tab: CAROUSELS */}
      {tab === "carousels" && plan.weekly_carousels && (
        <div className="space-y-3">
          <div className="bg-white rounded-2xl p-6 border border-[#E6E2DC]">
            <div className={TAG_CLS}>8 CARRUSELES · 2 POR SEMANA</div>
            <h3 className={SECTION_TITLE_CLS + " mt-2"}>Martes + Viernes · 20:00</h3>
            <p className="text-xs text-[#5C5E5C] mt-2 leading-relaxed max-w-2xl">
              Contenido SAVEABLE de alto valor: mitos rotos y antes/después con explicación clínica.
              Son los que más comparten amigas entre sí — el motor real de crecimiento orgánico.
            </p>
          </div>
          {plan.weekly_carousels.map((c) => {
            const isGen = c.status === "queued" || c.status === "generating";
            return (
            <div key={c.carousel_id} className={`bg-white rounded-xl border p-5 flex items-start gap-5 transition ${isGen ? "border-[#A69076] ring-2 ring-[#A69076]/30 shadow-md" : "border-[#E6E2DC]"}`}>
              <div className="flex-shrink-0 text-center w-20">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#A69076]">Semana</div>
                <div className="font-display text-3xl text-[#1A1C1A] leading-none mt-1">{c.week}</div>
                <div className="text-[10px] text-[#5C5E5C] mt-1 capitalize">{c.weekday}</div>
              </div>
              {/* Image thumbnail */}
              <button
                type="button"
                onClick={() => c.has_image && c.image_url && openPreview("carousel", c)}
                disabled={!c.has_image || !c.image_url}
                className={`flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-[#F4F0EB] border border-[#E6E2DC] flex items-center justify-center transition relative ${c.has_image && c.image_url ? "cursor-zoom-in hover:ring-2 hover:ring-[#7A9180]/40 hover:border-[#7A9180]/60" : "cursor-default"}`}
                data-testid={`carousel-thumb-btn-${c.carousel_id}`}
                title={c.has_image ? "Ver carrusel completo" : ""}
              >
                {c.has_image && c.image_url ? (
                  <>
                    <img
                      src={`${BACKEND}${c.image_url}`}
                      alt={c.topic}
                      className="w-full h-full object-cover"
                      data-testid={`carousel-img-${c.carousel_id}`}
                    />
                    {c.slide_count > 1 && (
                      <span className="absolute top-1 right-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded-full font-medium tracking-wider">
                        1/{c.slide_count}
                      </span>
                    )}
                  </>
                ) : c.generated && (c.status === "queued" || c.status === "generating") ? (
                  <Loader2 className="w-5 h-5 animate-spin text-[#A69076]" />
                ) : (
                  <Layers className="w-6 h-6 text-[#D9CDBC]" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span
                    className="text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-full font-medium"
                    style={{ color: "#7A9180", background: "#E6F0E8" }}
                  >
                    {c.template_id}
                  </span>
                  <span className="text-[10px] text-[#A69076]">Día {c.day} · {c.scheduled_time}</span>
                  {c.generated && c.status === "ready" && (
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#7A9180] flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Listo
                    </span>
                  )}
                  {isGen && (
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#A69076] flex items-center gap-1.5" data-testid={`carousel-status-${c.carousel_id}`}>
                      <Loader2 className="w-3 h-3 animate-spin" /> Generando · 30-60s
                    </span>
                  )}
                  {c.status === "failed" && (
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#D97757]">Error</span>
                  )}
                </div>
                <div className="text-sm text-[#1A1C1A] leading-relaxed font-medium">{c.topic}</div>
                <div className="text-[10px] text-[#A69076] mt-1 capitalize">Servicio: {c.service}</div>
                {isGen && (
                  <div className="mt-3 h-1.5 bg-[#EFEAE2] rounded-full overflow-hidden">
                    <div className="h-full bg-[#A69076] animate-pulse" style={{ width: "60%" }} />
                  </div>
                )}
                {c.caption && (
                  <div className="text-[10px] text-[#5C5E5C] mt-2 line-clamp-2 italic leading-relaxed">
                    {c.caption.slice(0, 160)}…
                  </div>
                )}
              </div>
              <div className="flex-shrink-0">
                <Button
                  size="sm"
                  variant={c.generated ? "outline" : "default"}
                  onClick={() => generateCarouselPreview(c.carousel_id)}
                  disabled={busyCarousel === c.carousel_id || isGen}
                  data-testid={`gen-carousel-${c.carousel_id}`}
                >
                  {busyCarousel === c.carousel_id || isGen ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Generando…</>
                  ) : c.generated ? (
                    "Regenerar"
                  ) : (
                    <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generar</>
                  )}
                </Button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Tab: AUTO-SCHEDULE */}
      {tab === "schedule" && (
        <div className="space-y-5">
          <div className="bg-gradient-to-br from-[#D97757] to-[#C66749] text-white rounded-2xl p-8">
            <Zap className="w-8 h-8 mb-3" />
            <h3 className="font-display text-3xl mb-3">Programación automática</h3>
            <p className="text-sm text-[#FCEFE8] max-w-xl leading-relaxed">
              Con 1 click programamos los 30 días completos: <strong>~68 piezas de contenido</strong>
              (30 Reels + 8 carruseles + 30 posts) generadas escalonadamente y publicadas en sus
              fechas y horarios óptimos.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-7 border border-[#E6E2DC] space-y-5">
            <div>
              <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">
                Fecha de inicio (día 1 = lunes recomendado)
              </Label>
              <Input
                type="date"
                value={autoSched.start_date}
                onChange={(e) => setAutoSched({ ...autoSched, start_date: e.target.value })}
                data-testid="auto-start-date"
              />
            </div>

            <div className="space-y-3 pt-3 border-t border-[#F4F0EB]">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-[#1A1C1A] font-medium">Reels (30 piezas)</div>
                  <div className="text-[10px] text-[#5C5E5C]">Publicación a las 12:00 hora local</div>
                </div>
                <Switch
                  checked={autoSched.include_reels}
                  onCheckedChange={(v) => setAutoSched({ ...autoSched, include_reels: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-[#1A1C1A] font-medium">Carruseles (8 piezas)</div>
                  <div className="text-[10px] text-[#5C5E5C]">Martes + Viernes a las 20:00</div>
                </div>
                <Switch
                  checked={autoSched.include_carousels}
                  onCheckedChange={(v) => setAutoSched({ ...autoSched, include_carousels: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-[#1A1C1A] font-medium">Posts diarios (30 piezas)</div>
                  <div className="text-[10px] text-[#5C5E5C]">Publicación a las 18:00 hora local</div>
                </div>
                <Switch
                  checked={autoSched.include_posts}
                  onCheckedChange={(v) => setAutoSched({ ...autoSched, include_posts: v })}
                />
              </div>
            </div>

            <div className="bg-[#FCEFE8] border border-[#F5D9CC] rounded-lg p-4 text-xs text-[#7a3a3a] leading-relaxed">
              <strong>Importante:</strong> Esto consumirá cuota de Sora 2 (videos) y Gemini Nano Banana
              (imágenes). Los Reels se generan escalonadamente (3 cada 2 min) para respetar rate limits.
              La publicación en Meta es automática vía el scheduler interno.
            </div>

            <Button
              onClick={triggerAutoSchedule}
              disabled={autoBusy}
              size="lg"
              className="w-full"
              data-testid="auto-schedule-submit"
            >
              {autoBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Zap className="w-4 h-4 mr-2" /> Programar 30 días automáticamente</>}
            </Button>

            <div className="text-center text-[10px] tracking-[0.2em] uppercase text-[#A69076] my-2">— O si ya tienes el contenido generado —</div>

            <Button
              onClick={triggerReschedule}
              disabled={reschedBusy}
              size="lg"
              variant="outline"
              className="w-full border-[#1A1C1A] text-[#1A1C1A] hover:bg-[#F4F0EB]"
              data-testid="reschedule-submit"
            >
              {reschedBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Calendar className="w-4 h-4 mr-2" /> Re-programar contenido existente (Día 1 = {autoSched.start_date})</>}
            </Button>
            <p className="text-[10px] text-[#A69076] text-center leading-relaxed">
              Sólo actualiza las fechas de publicación de las piezas ya generadas. No consume cuota de IA.
            </p>
          </div>
        </div>
      )}

      {/* Tab: MUSIC LIBRARY */}
      {tab === "music" && (
        <div className="space-y-6" data-testid="music-tab">
          <div className="bg-white rounded-2xl p-7 border border-[#E6E2DC]">
            <div className={TAG_CLS}>BIBLIOTECA DE MÚSICA</div>
            <h3 className={SECTION_TITLE_CLS + " mt-2"}>Tracks de cada Reel</h3>
            <p className="text-xs text-[#5C5E5C] mt-2 leading-relaxed max-w-2xl">
              El generador alterna automáticamente entre los tracks de cada categoría para evitar repetición.
              Sube tu propia música o usa los royalty-free incluidos.
              Recomendado: 5+ tracks por categoría para máxima variedad en los 30 días.
            </p>
          </div>

          {!musicLib && (
            <div className="bg-white rounded-2xl p-8 border border-[#E6E2DC] text-center text-sm text-[#5C5E5C]">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
              Cargando biblioteca...
            </div>
          )}

          {musicLib?.categories.map((cat) => (
            <div
              key={cat.id}
              className="bg-white rounded-2xl p-6 border border-[#E6E2DC]"
              data-testid={`music-cat-${cat.id}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h4 className="font-display text-xl text-[#1A1C1A]">{cat.label}</h4>
                    <span
                      className="text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-full font-medium"
                      style={{
                        color: cat.count >= 5 ? "#5C7560" : cat.count >= 3 ? "#7A6940" : "#9C4848",
                        background: cat.count >= 5 ? "#E6F0E8" : cat.count >= 3 ? "#F4EDDC" : "#F4DCDC",
                      }}
                    >
                      {cat.count} {cat.count === 1 ? "track" : "tracks"}
                    </span>
                  </div>
                  {cat.default_service?.length > 0 && (
                    <div className="text-[11px] text-[#A69076] mt-1 capitalize">
                      Default para: {cat.default_service.join(", ")}
                    </div>
                  )}
                  {cat.credit && (
                    <div className="text-[10px] text-[#A69076] italic mt-1">{cat.credit}</div>
                  )}
                </div>

                {/* Upload button */}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="audio/mpeg,audio/mp3,audio/wav,audio/m4a,audio/ogg,.mp3,.wav,.m4a,.ogg"
                    className="hidden"
                    disabled={musicBusy === cat.id}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (f) uploadMusic(cat.id, f);
                    }}
                    data-testid={`music-upload-${cat.id}`}
                  />
                  <span
                    className={`inline-flex items-center gap-2 px-4 py-2 text-xs rounded-lg border transition ${
                      musicBusy === cat.id
                        ? "bg-[#F4F0EB] border-[#E6E2DC] text-[#A69076] cursor-wait"
                        : "bg-[#1A1C1A] border-[#1A1C1A] text-white hover:bg-[#2a2b29]"
                    }`}
                  >
                    {musicBusy === cat.id ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Subiendo...</>
                    ) : (
                      <><Sparkles className="w-3.5 h-3.5" /> Subir track</>
                    )}
                  </span>
                </label>
              </div>

              {cat.tracks.length === 0 ? (
                <div className="text-xs text-[#A69076] italic py-4 text-center">
                  Sin tracks aún. Sube uno para empezar.
                </div>
              ) : (
                <div className="space-y-2">
                  {cat.tracks.map((t) => (
                    <div
                      key={t.filename}
                      className="flex items-center gap-3 bg-[#FAF7F2] rounded-lg p-3 border border-[#E6E2DC]"
                      data-testid={`music-track-${t.filename}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[#1A1C1A] font-medium truncate">{t.filename}</div>
                        <div className="text-[10px] text-[#A69076]">{t.size_kb} KB</div>
                      </div>
                      <audio
                        controls
                        preload="none"
                        className="h-9"
                        style={{ maxWidth: "320px" }}
                        src={`${BACKEND}${t.url}`}
                      >
                        Tu navegador no soporta audio.
                      </audio>
                      <button
                        type="button"
                        onClick={() => deleteMusic(t.filename)}
                        className="text-[#9C4848] hover:bg-[#F4DCDC] p-2 rounded-lg transition"
                        title="Eliminar"
                        data-testid={`music-delete-${t.filename}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Quick tip */}
          <div className="bg-[#F4F0EB] rounded-2xl p-5 border border-[#E6E2DC]">
            <div className="flex items-start gap-3">
              <Sparkles className="w-4 h-4 text-[#D97757] flex-shrink-0 mt-0.5" />
              <div className="text-xs text-[#5C5E5C] leading-relaxed">
                <strong className="text-[#1A1C1A]">Tip:</strong> Para mejor calidad, usa MP3/WAV de 20-90s,
                bitrate 128-256 kbps. Formatos aceptados: <code className="bg-white px-1.5 py-0.5 rounded">.mp3 .wav .m4a .ogg</code>.
                Máx 25 MB por archivo. Royalty-free recomendado: <a className="underline" href="https://pixabay.com/music/" target="_blank" rel="noreferrer">Pixabay Music</a>, <a className="underline" href="https://incompetech.com/music/royalty-free/music.html" target="_blank" rel="noreferrer">Incompetech</a>.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: DM */}
      {tab === "dm" && (
        <div className="space-y-6">
          {/* Status */}
          <div className="bg-white rounded-2xl p-7 border border-[#E6E2DC]">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className={TAG_CLS}>AUTO-RESPONDER</div>
                <h3 className={SECTION_TITLE_CLS + " mt-2"}>
                  Respuesta automática «{dmCfg.keyword}»
                </h3>
                <p className="text-xs text-[#5C5E5C] mt-2 max-w-lg leading-relaxed">
                  Cuando alguien envíe esta palabra en un DM o comentario, la app responde
                  automáticamente con tu mensaje pre-armado y el link a WhatsApp.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={dmCfg.enabled}
                  onCheckedChange={(v) => setDmCfg({ ...dmCfg, enabled: v })}
                  data-testid="dm-enabled-switch"
                />
                <span className="text-sm text-[#1A1C1A]">{dmCfg.enabled ? "Activo" : "Pausado"}</span>
              </div>
            </div>
            {!metaConfigured && (
              <div className="mt-5 bg-[#F5E0E0] border border-[#E5C4C4] rounded-lg p-3 text-xs text-[#7a3a3a]">
                Meta no está configurado. Conecta Facebook/Instagram para activar la auto-respuesta.
              </div>
            )}
          </div>

          {/* Config */}
          <div className="bg-white rounded-2xl p-7 border border-[#E6E2DC] space-y-5">
            <div>
              <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">
                Palabra clave (mayúsculas o minúsculas)
              </Label>
              <Input
                value={dmCfg.keyword}
                onChange={(e) => setDmCfg({ ...dmCfg, keyword: e.target.value.toUpperCase() })}
                data-testid="dm-keyword-input"
              />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">
                Mensaje automático
              </Label>
              <Textarea
                rows={8}
                value={dmCfg.reply_text}
                onChange={(e) => setDmCfg({ ...dmCfg, reply_text: e.target.value })}
                data-testid="dm-reply-input"
              />
              <p className="text-[10px] text-[#A69076] mt-2 italic">
                Incluye tu link de WhatsApp. La paciente toca el link y entra directo a la conversación.
              </p>
            </div>
            <div className="flex gap-3">
              <Button onClick={saveDM} disabled={busyDM} data-testid="dm-save-btn">
                {busyDM ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
              </Button>
              <Button variant="outline" onClick={testDMNow} disabled={busyDM} data-testid="dm-test-btn">
                {busyDM ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" /> Probar ahora</>}
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white rounded-2xl p-7 border border-[#E6E2DC]">
            <div className={TAG_CLS}>ACTIVIDAD</div>
            <h3 className={SECTION_TITLE_CLS + " mt-2 mb-5"}>Respuestas enviadas</h3>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-[#F4F0EB] rounded-xl p-5 text-center">
                <div className="font-display text-4xl text-[#1A1C1A]">{dmStats.total_sent}</div>
                <div className={TAG_CLS}>enviadas</div>
              </div>
              <div className="bg-[#F5E0E0] rounded-xl p-5 text-center">
                <div className="font-display text-4xl text-[#7a3a3a]">{dmStats.total_failed}</div>
                <div className={TAG_CLS}>fallaron</div>
              </div>
            </div>
            {dmStats.recent.length > 0 ? (
              <div className="space-y-2">
                <div className={TAG_CLS + " mb-2"}>Últimas respuestas</div>
                {dmStats.recent.slice(0, 5).map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-2 border-b border-[#F4F0EB] last:border-0">
                    <div>
                      <span className="text-[#1A1C1A] font-medium">{r.sender_name || "Usuario"}</span>
                      <span className="text-[#A69076] ml-2">({r.platform})</span>
                    </div>
                    <span className="text-[#5C5E5C]">{new Date(r.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-xs text-[#A69076] italic">
                Aún no hay respuestas. El sistema revisa cada 5 minutos.
              </div>
            )}
          </div>
        </div>
      )}
      {/* Preview Modal */}
      {previewDay && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 lg:p-8"
          data-testid="launch-preview-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col lg:flex-row"
          >
            {/* Video side */}
            <div className="bg-[#1A1C1A] flex items-center justify-center p-6 lg:w-[420px]">
              {previewDay.reel?.status === "generating" ? (
                <div className="text-center text-[#D9CDBC] py-20">
                  <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" />
                  <div className="text-sm">Generando con Sora 2…</div>
                  <div className="text-[10px] text-[#A69076] mt-2">Tarda entre 2 y 5 min</div>
                </div>
              ) : previewDay.reel?.video_url ? (
                <video
                  src={`${BACKEND}${previewDay.reel.video_url}`}
                  controls
                  autoPlay
                  className="w-full max-h-[70vh] rounded-lg"
                  data-testid="launch-preview-video"
                />
              ) : previewDay.reel?.thumb_url ? (
                <img
                  src={`${BACKEND}${previewDay.reel.thumb_url}`}
                  alt="Preview"
                  className="w-full max-h-[70vh] rounded-lg object-contain"
                />
              ) : (
                <div className="text-[#A69076] text-sm py-20">Sin video disponible</div>
              )}
            </div>
            {/* Info side */}
            <div className="flex-1 p-6 lg:p-8 overflow-y-auto">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <div className={TAG_CLS}>Día {previewDay.day} · {TYPE_LABEL[previewDay.type]?.label}</div>
                  <h3 className="font-display text-2xl text-[#1A1C1A] mt-2 leading-snug">
                    {previewDay.hook || previewDay.topic}
                  </h3>
                </div>
                <button
                  onClick={() => setPreviewDay(null)}
                  className="text-[#5C5E5C] hover:text-[#1A1C1A] p-1"
                  data-testid="launch-preview-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {previewDay.problem && (
                <div className="mb-3 text-sm">
                  <span className={TAG_CLS}>Problema</span>
                  <p className="text-[#1A1C1A] mt-1 leading-relaxed">{previewDay.problem}</p>
                </div>
              )}
              {previewDay.solution && (
                <div className="mb-3 text-sm">
                  <span className={TAG_CLS}>Solución</span>
                  <p className="text-[#1A1C1A] mt-1 leading-relaxed">{previewDay.solution}</p>
                </div>
              )}
              {previewDay.cta && (
                <div className="mb-5 text-sm bg-[#FCEFE8] border border-[#F5D9CC] rounded-lg p-3">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#D97757]">CTA</span>
                  <p className="text-[#1A1C1A] mt-1 italic">{previewDay.cta}</p>
                </div>
              )}

              {previewDay.reel?.caption && (
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className={TAG_CLS}>Caption (Instagram)</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyText(previewDay.reel.caption)}
                    >
                      <Copy className="w-3.5 h-3.5 mr-1.5" /> Copiar
                    </Button>
                  </div>
                  <div className="bg-[#F4F0EB] rounded-lg p-4 text-sm whitespace-pre-line text-[#1A1C1A] leading-relaxed max-h-60 overflow-y-auto">
                    {previewDay.reel.caption}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-3 border-t border-[#E6E2DC]">
                {previewDay.reel?.video_url && (
                  <a
                    href={`${BACKEND}${previewDay.reel.video_url}`}
                    download
                    className="inline-flex items-center gap-2 text-xs bg-[#1A1C1A] text-[#F4F0EB] px-4 py-2 rounded-lg hover:bg-[#2a2b29]"
                  >
                    Descargar video
                  </a>
                )}
                <Button variant="outline" size="sm" onClick={() => openEditor(previewDay)}>
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Editar y regenerar
                </Button>
                <a
                  href="https://www.instagram.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-xs bg-[#D97757] text-white px-4 py-2 rounded-lg hover:bg-[#C66749]"
                >
                  Abrir Instagram
                </a>
              </div>
              <p className="text-[10px] text-[#A69076] italic mt-3 leading-relaxed">
                Tip: descarga el video, ábrelo en Instagram → Reels y pega el caption.
                Si quieres audio trending, agrégalo desde el editor de Reels en la app de IG.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Editor Modal — edit the prompt before generating/regenerating */}
      {editDay && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 lg:p-8 overflow-y-auto"
          data-testid="launch-editor-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white z-10 px-7 pt-7 pb-4 border-b border-[#E6E2DC] flex items-start justify-between gap-4">
              <div>
                <div className={TAG_CLS}>Día {editDay.day} · {TYPE_LABEL[editDay.type]?.label}</div>
                <h3 className="font-display text-2xl text-[#1A1C1A] mt-1">
                  Editar prompt antes de {editDay.generated ? "regenerar" : "generar"}
                </h3>
                <p className="text-xs text-[#5C5E5C] mt-2 leading-relaxed">
                  Afina cada parte para que Sora muestre exactamente lo que quieres.
                </p>
              </div>
              <button onClick={() => setEditDay(null)} className="text-[#5C5E5C] hover:text-[#1A1C1A] p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-7 py-6 space-y-5">
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">
                  Hook (primera línea del caption)
                </Label>
                <Textarea
                  rows={2}
                  value={editForm.custom_hook}
                  onChange={(e) => setEditForm({ ...editForm, custom_hook: e.target.value })}
                  data-testid="edit-hook"
                />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">
                  Problema
                </Label>
                <Textarea
                  rows={2}
                  value={editForm.custom_problem}
                  onChange={(e) => setEditForm({ ...editForm, custom_problem: e.target.value })}
                  data-testid="edit-problem"
                />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">
                  Solución
                </Label>
                <Textarea
                  rows={2}
                  value={editForm.custom_solution}
                  onChange={(e) => setEditForm({ ...editForm, custom_solution: e.target.value })}
                  data-testid="edit-solution"
                />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">
                  CTA (llamado a la acción)
                </Label>
                <Input
                  value={editForm.custom_cta}
                  onChange={(e) => setEditForm({ ...editForm, custom_cta: e.target.value })}
                  data-testid="edit-cta"
                />
              </div>

              {/* THE KEY field — extra visual instructions for Sora */}
              <div className="bg-[#FCEFE8] border border-[#F5D9CC] rounded-lg p-4">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-[#D97757] font-medium mb-2 block flex items-center gap-2">
                  <Sparkles className="w-3 h-3" /> Instrucciones visuales extra
                </Label>
                <p className="text-[10px] text-[#5C5E5C] mb-3 italic leading-relaxed">
                  Describe lo que la IA NO está captando bien. Ej: «la mujer debe tener ojeras pronunciadas
                  desde el segundo 0», «mostrar las manchas en el cuello», «que se vea el doble mentón al inicio».
                  Esto se inyecta al inicio del prompt de Sora con prioridad alta.
                </p>
                <Textarea
                  rows={4}
                  placeholder="Ej: La mujer debe verse cansada con ojeras profundas y bolsas visibles desde el primer segundo. Después del tratamiento, los ojos se ven descansados pero NO retocados."
                  value={editForm.extra_visual}
                  onChange={(e) => setEditForm({ ...editForm, extra_visual: e.target.value })}
                  data-testid="edit-extra-visual"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">
                    Casting
                  </Label>
                  <select
                    value={editForm.casting}
                    onChange={(e) => setEditForm({ ...editForm, casting: e.target.value })}
                    className="w-full border border-[#E6E2DC] rounded-lg px-3 py-2 text-sm bg-white"
                    data-testid="edit-casting"
                  >
                    <option value="">Auto (rotación)</option>
                    <option value="latina_mx">Latina mexicana</option>
                    <option value="rubia_eu">Rubia europea</option>
                    <option value="pelirroja">Pelirroja</option>
                    <option value="japonesa">Japonesa</option>
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">
                    Música
                  </Label>
                  <select
                    value={editForm.music_category}
                    onChange={(e) => setEditForm({ ...editForm, music_category: e.target.value })}
                    className="w-full border border-[#E6E2DC] rounded-lg px-3 py-2 text-sm bg-white"
                    data-testid="edit-music"
                  >
                    <option value="auto">Auto (según servicio)</option>
                    <option value="calm_spa">Calm Spa</option>
                    <option value="upbeat_lounge">Upbeat Lounge</option>
                    <option value="cinematic">Cinematic</option>
                    <option value="acoustic_warm">Acoustic Warm</option>
                    <option value="silent">Sin música</option>
                  </select>
                </div>
              </div>

              {/* FULL PROMPT EDITOR — admin-level control */}
              <div className="bg-[#F4F0EB] border border-[#E6E2DC] rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[10px] tracking-[0.2em] uppercase text-[#1A1C1A] font-medium flex items-center gap-2">
                    <FileText className="w-3 h-3" /> Prompt completo de Sora (modo avanzado)
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={refreshPromptPreview}
                    type="button"
                    data-testid="edit-refresh-prompt"
                  >
                    <Sparkles className="w-3 h-3 mr-1" /> Regenerar desde campos
                  </Button>
                </div>
                <p className="text-[10px] text-[#5C5E5C] mb-3 italic leading-relaxed">
                  Este es el texto exacto que se le envía a Sora 2. Edítalo libremente para corregir
                  cualquier parte (inicio, medio o final del video). Si lo modificas a mano, marca la
                  casilla de abajo para que se use TU versión verbatim.
                </p>
                <Textarea
                  rows={14}
                  value={editForm.full_prompt}
                  onChange={(e) => setEditForm({ ...editForm, full_prompt: e.target.value, use_custom_prompt: true })}
                  className="font-mono text-[11px] leading-relaxed"
                  data-testid="edit-full-prompt"
                  placeholder="Cargando prompt..."
                />
                <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
                  <label className="flex items-center gap-2 text-xs text-[#1A1C1A] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editForm.use_custom_prompt}
                      onChange={(e) => setEditForm({ ...editForm, use_custom_prompt: e.target.checked })}
                      className="accent-[#D97757]"
                      data-testid="edit-use-custom-prompt"
                    />
                    <span>Usar mi versión editada (verbatim)</span>
                  </label>
                  <div className="text-[10px] text-[#A69076]">
                    {editForm.full_prompt?.length || 0} caracteres
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-[#E6E2DC] px-7 py-4 flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setEditDay(null)}>Cancelar</Button>
              <Button
                onClick={submitGenerate}
                disabled={busyDay === editDay.day}
                data-testid="edit-submit"
              >
                {busyDay === editDay.day ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <><Sparkles className="w-4 h-4 mr-1.5" /> {editDay.generated ? "Regenerar Reel" : "Generar Reel"}</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ---- POST / CAROUSEL PREVIEW MODAL ---- */}
      <Dialog open={!!preview} onOpenChange={(open) => { if (!open) closePreview(); }}>
        <DialogContent className="max-w-5xl p-0 overflow-hidden bg-white" data-testid="preview-modal">
          {preview && (
            <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr] max-h-[90vh]">
              {/* Left: image viewer */}
              <div className="bg-[#1A1C1A] flex items-center justify-center relative aspect-square md:aspect-auto md:min-h-[520px]">
                {(() => {
                  const isCarousel = preview.type === "carousel";
                  const urls = isCarousel
                    ? (preview.data.slide_urls || (preview.data.image_url ? [preview.data.image_url] : []))
                    : (preview.data.image_url ? [preview.data.image_url] : []);
                  const url = urls[preview.slideIdx];
                  if (!url) {
                    return <div className="text-white/40 text-sm">Sin imagen disponible</div>;
                  }
                  return (
                    <img
                      src={`${BACKEND}${url}`}
                      alt="preview"
                      className="max-w-full max-h-[90vh] object-contain"
                      data-testid="preview-img"
                    />
                  );
                })()}
                {/* carousel nav arrows */}
                {preview.type === "carousel" && (preview.data.slide_urls?.length || 0) > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={prevSlide}
                      className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition"
                      data-testid="preview-prev"
                      aria-label="Slide anterior"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={nextSlide}
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition"
                      data-testid="preview-next"
                      aria-label="Slide siguiente"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full font-medium tracking-wider">
                      Slide {preview.slideIdx + 1} / {preview.data.slide_urls.length}
                    </div>
                  </>
                )}
              </div>

              {/* Right: info pane */}
              <div className="flex flex-col overflow-hidden">
                <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E6E2DC]">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span
                      className="text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-full font-medium inline-block"
                      style={{
                        color: preview.type === "carousel" ? "#7A9180" : "#5C5E5C",
                        background: preview.type === "carousel" ? "#E6F0E8" : "#F4F0EB",
                      }}
                    >
                      {preview.type === "carousel" ? "Carrusel" : (preview.data.type || "Post")}
                    </span>
                    {preview.type === "carousel" && (
                      <span className="text-[10px] text-[#A69076]">
                        Semana {preview.data.week} · {preview.data.weekday}
                      </span>
                    )}
                    <span className="text-[10px] text-[#A69076]">
                      Día {preview.data.day} · {preview.data.scheduled_time}
                    </span>
                  </div>
                  <DialogTitle className="font-display text-2xl text-[#1A1C1A] leading-tight">
                    {preview.data.topic}
                  </DialogTitle>
                  <DialogDescription className="text-xs text-[#A69076] capitalize mt-1">
                    Servicio: {preview.data.service}
                    {preview.data.template_id && ` · Plantilla: ${preview.data.template_id}`}
                  </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                  {/* Caption (view or edit) */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className={TAG_CLS}>Caption</div>
                      {!editingCaption && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCaptionDraft(preview.data.caption || "");
                            setHashtagsDraft((preview.data.hashtags || []).join(" "));
                            setEditingCaption(true);
                          }}
                          className="text-xs h-7"
                          data-testid="preview-edit-caption-btn"
                          disabled={!_previewDbId()}
                        >
                          <Sparkles className="w-3 h-3 mr-1.5" /> Editar
                        </Button>
                      )}
                    </div>
                    {editingCaption ? (
                      <div className="space-y-2">
                        <Textarea
                          value={captionDraft}
                          onChange={(e) => setCaptionDraft(e.target.value)}
                          rows={7}
                          className="text-sm"
                          data-testid="preview-caption-input"
                        />
                        <Input
                          value={hashtagsDraft}
                          onChange={(e) => setHashtagsDraft(e.target.value)}
                          placeholder="Hashtags separados por espacio (sin #)"
                          className="text-sm"
                          data-testid="preview-hashtags-input"
                        />
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingCaption(false)}
                            disabled={savingCaption}
                          >
                            Cancelar
                          </Button>
                          <Button
                            size="sm"
                            onClick={saveCaptionEdit}
                            disabled={savingCaption}
                            data-testid="preview-save-caption"
                          >
                            {savingCaption ? (
                              <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Guardando</>
                            ) : "Guardar"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="text-sm text-[#1A1C1A] leading-relaxed whitespace-pre-wrap bg-[#FAF7F2] rounded-lg p-4 border border-[#E6E2DC]" data-testid="preview-caption">
                          {preview.data.caption || <span className="text-[#A69076] italic">Sin caption generado</span>}
                        </div>
                        {preview.data.caption && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyText(preview.data.caption)}
                            className="mt-2 text-xs h-7"
                            data-testid="preview-copy-caption"
                          >
                            <Copy className="w-3 h-3 mr-1.5" /> Copiar caption
                          </Button>
                        )}
                      </>
                    )}
                  </div>

                  {/* Hashtags */}
                  {preview.data.hashtags && preview.data.hashtags.length > 0 && (
                    <div>
                      <div className={TAG_CLS + " mb-2"}>Hashtags ({preview.data.hashtags.length})</div>
                      <div className="flex flex-wrap gap-1.5" data-testid="preview-hashtags">
                        {preview.data.hashtags.map((h, i) => (
                          <span
                            key={i}
                            className="text-[11px] px-2 py-1 rounded-full bg-[#E6F0E8] text-[#5C7560] font-medium"
                          >
                            #{h}
                          </span>
                        ))}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyText(preview.data.hashtags.map((h) => "#" + h).join(" "))}
                        className="mt-2 text-xs h-7"
                        data-testid="preview-copy-hashtags"
                      >
                        <Copy className="w-3 h-3 mr-1.5" /> Copiar hashtags
                      </Button>
                    </div>
                  )}

                  {/* Texts per slide (carousel only) */}
                  {preview.type === "carousel" && preview.data.slide_texts?.length > 0 && (
                    <div>
                      <div className={TAG_CLS + " mb-2"}>Texto del slide actual</div>
                      <div className="text-sm text-[#1A1C1A] leading-relaxed bg-[#FAF7F2] rounded-lg p-4 border border-[#E6E2DC] italic">
                        {preview.data.slide_texts[preview.slideIdx] || "—"}
                      </div>
                    </div>
                  )}

                  {/* Schedule + status */}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className={TAG_CLS + " mb-1"}>Estado</div>
                      <div className="text-[#1A1C1A] font-medium capitalize">
                        {preview.data.status || "borrador"}
                      </div>
                    </div>
                    <div>
                      <div className={TAG_CLS + " mb-1"}>Programado</div>
                      <div className="text-[#1A1C1A] flex items-center gap-1.5">
                        <Clock className="w-3 h-3 text-[#A69076]" />
                        {preview.data.scheduled_for
                          ? new Date(preview.data.scheduled_for).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })
                          : "Sin programar"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer actions */}
                <div className="border-t border-[#E6E2DC] px-6 py-4 flex flex-wrap gap-2 justify-end bg-[#FAF7F2]">
                  <label
                    className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-[#E6E2DC] cursor-pointer transition ${
                      uploadingImg || !_previewDbId()
                        ? "opacity-50 cursor-not-allowed bg-[#F4F0EB]"
                        : "bg-white hover:bg-[#F4F0EB] hover:border-[#A69076]"
                    }`}
                    data-testid="preview-upload-image-label"
                    title={
                      !_previewDbId()
                        ? "Genera primero el contenido"
                        : preview.type === "carousel"
                          ? `Reemplazar slide ${preview.slideIdx + 1}`
                          : "Subir imagen custom"
                    }
                  >
                    {uploadingImg ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Subiendo…</>
                    ) : (
                      <><ImageIcon className="w-3.5 h-3.5" />
                        {preview.type === "carousel"
                          ? `Subir imagen · Slide ${preview.slideIdx + 1}`
                          : "Subir imagen custom"}</>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploadingImg || !_previewDbId()}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadCustomImage(f);
                        e.target.value = "";
                      }}
                      data-testid="preview-upload-image-input"
                    />
                  </label>
                  <Button
                    variant="outline"
                    onClick={() => publishNowToFacebook()}
                    disabled={fbPublishing || !_previewDbId()}
                    className="text-xs h-auto py-2 px-3 border-[#1877F2] text-[#1877F2] hover:bg-[#1877F2]/10"
                    data-testid="preview-publish-fb"
                    title="Publicar esta pieza directo en la página de Facebook"
                  >
                    {fbPublishing ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Publicando…</>
                    ) : (
                      <><Send className="w-3.5 h-3.5 mr-1.5" /> Publicar ahora en FB</>
                    )}
                  </Button>
                  <Button variant="outline" onClick={closePreview} data-testid="preview-close">
                    Cerrar
                  </Button>
                  {preview.type === "post" && preview.data._is_lifestyle ? (
                    <Button
                      variant="outline"
                      onClick={() => { closePreview(); }}
                      data-testid="preview-open-lifestyle-tab"
                      title="Edita este post en el panel de Lifestyle"
                    >
                      Editar en Lifestyle
                    </Button>
                  ) : preview.type === "post" ? (
                    <Button
                      onClick={() => { closePreview(); generatePostPreview(preview.data.day); }}
                      disabled={busyPost === preview.data.day}
                      data-testid="preview-regenerate-post"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Regenerar
                    </Button>
                  ) : (
                    <Button
                      onClick={() => { closePreview(); generateCarouselPreview(preview.data.carousel_id); }}
                      disabled={busyCarousel === preview.data.carousel_id}
                      data-testid="preview-regenerate-carousel"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Regenerar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============================================================
// Helpers + Sub-components for Setup tab (Highlight covers + Story hooks)
// ============================================================
function launch_setup_highlight_placeholders(plan) {
  if (!plan) return [];
  return plan.setup.highlights.map((h) => ({
    highlight_id: h.id, label: h.label, subtitle: h.description, status: "not_generated",
  }));
}

function HighlightCoverCard({ cover, currentPalette = "default", onPaletteChange, busy = false }) {
  const url = cover.image_url ? `${BACKEND}${cover.image_url}` : null;
  const dlUrl = url ? `${url}?download=1` : null;
  const ready = cover.status === "ready" && url;
  const queued = cover.status === "queued";
  const failed = cover.status === "failed";
  const PALETTE_CHIPS = [
    { id: "default", label: "Brand", swatch: "#1A1C1A", ring: "#D97757" },
    { id: "neon_pink", label: "Rosa", swatch: "#FF2E88", ring: "#FFF" },
    { id: "neon_green", label: "Verde", swatch: "#39FF14", ring: "#1A1C1A" },
  ];
  return (
    <div
      className="bg-[#F4F0EB] rounded-xl overflow-hidden border border-[#E6E2DC] flex flex-col"
      data-testid={`launch-cover-${cover.highlight_id}`}
    >
      <div className="aspect-[9/16] bg-[#EDE5DA] relative flex items-center justify-center">
        {ready && <img src={url} alt={cover.label} className="absolute inset-0 w-full h-full object-cover" />}
        {(queued || busy) && <Loader2 className="w-6 h-6 text-[#A69076] animate-spin absolute" />}
        {failed && (
          <div className="text-[10px] text-[#965D5D] text-center px-2 leading-tight">
            Falló · vuelve a intentar
          </div>
        )}
        {!ready && !queued && !failed && !busy && (
          <div className="text-[10px] text-[#A69076] tracking-[0.2em] uppercase">Sin generar</div>
        )}
      </div>
      <div className="p-3 flex flex-col gap-2">
        <div>
          <div className="font-display text-base text-[#1A1C1A]">{cover.label}</div>
          <div className="text-[10px] text-[#5C5E5C] leading-snug mt-0.5">{cover.subtitle}</div>
        </div>
        {/* Palette chips */}
        {onPaletteChange && (
          <div className="flex gap-1.5 items-center" data-testid={`launch-cover-palette-${cover.highlight_id}`}>
            {PALETTE_CHIPS.map((p) => {
              const active = p.id === currentPalette;
              return (
                <button
                  key={p.id}
                  type="button"
                  disabled={busy}
                  onClick={() => onPaletteChange(p.id)}
                  className={`w-5 h-5 rounded-full transition-all ${
                    active
                      ? "ring-2 ring-offset-1 ring-[#1A1C1A] scale-110"
                      : "ring-1 ring-[#E6E2DC] hover:scale-105"
                  } ${busy ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  style={{ background: p.swatch }}
                  title={p.label + (active ? " (activo)" : "")}
                  aria-label={p.label}
                  data-testid={`launch-cover-palette-${cover.highlight_id}-${p.id}`}
                />
              );
            })}
            <span className="text-[9px] text-[#A69076] uppercase tracking-widest ml-1">
              {currentPalette === "neon_pink" ? "Neón rosa" : currentPalette === "neon_green" ? "Neón verde" : "Brand"}
            </span>
          </div>
        )}
        {ready && (
          <a
            href={dlUrl}
            download
            className="inline-flex items-center justify-center gap-1.5 h-8 rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white text-[10px] tracking-[0.2em] uppercase transition-colors"
            data-testid={`launch-cover-dl-${cover.highlight_id}`}
          >
            <Download className="w-3 h-3" /> Descargar
          </a>
        )}
      </div>
    </div>
  );
}

function HighlightStoryCard({ story, onCopy, kind }) {
  const url = story.image_url ? `${BACKEND}${story.image_url}` : null;
  const dlUrl = url ? `${url}?download=1` : null;
  const ready = story.status === "ready" && url;
  const queued = story.status === "queued";
  const failed = story.status === "failed";
  const title = story.title || "";
  const caption = story.caption || "";
  const tags = (Array.isArray(story.hashtags) ? story.hashtags : []).map((h) => `#${String(h).replace(/^#/, "")}`).join(" ");
  const fullText = `${title}\n\n${caption}\n\n${tags}`.trim();
  return (
    <div
      className="bg-[#F4F0EB] rounded-xl overflow-hidden border border-[#E6E2DC] flex flex-col"
      data-testid={`launch-hl-${kind}-${story.id}`}
    >
      <div className="aspect-[9/16] bg-[#EDE5DA] relative flex items-center justify-center">
        {ready && <img src={url} alt={title} className="absolute inset-0 w-full h-full object-cover" />}
        {queued && <Loader2 className="w-6 h-6 text-[#A69076] animate-spin" />}
        {failed && (
          <div className="text-[10px] text-[#965D5D] text-center px-2 leading-tight">
            Falló · vuelve a intentar
          </div>
        )}
        {!ready && !queued && !failed && (
          <div className="text-[10px] text-[#A69076] tracking-[0.2em] uppercase">Sin generar</div>
        )}
      </div>
      <div className="p-3 flex flex-col gap-2 flex-1">
        <div className="font-display text-sm leading-tight text-[#1A1C1A] line-clamp-2">{title}</div>
        {caption && (
          <div className="text-[10px] text-[#5C5E5C] leading-snug line-clamp-3">{caption}</div>
        )}
        <div className="flex gap-1.5 mt-auto pt-2">
          {ready && (
            <a
              href={dlUrl}
              download
              className="flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white text-[10px] tracking-[0.2em] uppercase transition-colors"
              data-testid={`launch-hl-dl-${story.id}`}
              title="Descargar imagen lista para subir a IG"
            >
              <Download className="w-3 h-3" /> Descargar
            </a>
          )}
          {(caption || tags) && (
            <button
              onClick={() => onCopy(fullText)}
              className="inline-flex items-center justify-center h-8 px-2.5 rounded-full bg-white border border-[#E6E2DC] hover:border-[#A69076] text-[10px] tracking-[0.2em] uppercase text-[#1A1C1A]"
              title="Copiar caption + hashtags"
              data-testid={`launch-hl-copy-${story.id}`}
            >
              <Copy className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StoryHookCard({ story, onCopy }) {
  const url = story.image_url ? `${BACKEND}${story.image_url}` : null;
  const dlUrl = url ? `${url}?download=1` : null;
  const dlRaw = url ? `${url}?download=1&variant=raw` : null;
  const ready = story.status === "ready" && url;
  const queued = story.status === "queued";
  const failed = story.status === "failed";
  const fullText = `${story.headline}\n\n${story.body}\n\n${story.cta}`;
  return (
    <div
      className="bg-[#F4F0EB] rounded-xl overflow-hidden border border-[#E6E2DC] flex flex-col"
      data-testid={`launch-story-${story.story_id}`}
    >
      <div className="aspect-[9/16] bg-[#EDE5DA] relative flex items-center justify-center">
        {ready && <img src={url} alt={story.headline} className="absolute inset-0 w-full h-full object-cover" />}
        {queued && <Loader2 className="w-6 h-6 text-[#A69076] animate-spin" />}
        {failed && (
          <div className="text-[10px] text-[#965D5D] text-center px-2 leading-tight">
            Falló · vuelve a intentar
          </div>
        )}
        {!ready && !queued && !failed && (
          <div className="text-[10px] text-[#A69076] tracking-[0.2em] uppercase">Sin generar</div>
        )}
        {ready && (
          <div className="absolute top-2 left-2 bg-[#1A1C1A]/85 text-white text-[9px] tracking-[0.2em] uppercase px-2 py-1 rounded-full">
            {story.service}
          </div>
        )}
      </div>
      <div className="p-3 flex flex-col gap-2 flex-1">
        <div className="font-display text-sm leading-tight text-[#1A1C1A]">{story.headline}</div>
        <div className="text-[11px] text-[#5C5E5C] leading-snug">{story.body}</div>
        <div className="text-[11px] text-[#A69076] leading-snug font-medium">{story.cta}</div>
        <div className="flex gap-1.5 mt-auto pt-2">
          {ready && (
            <a
              href={dlUrl}
              download
              className="flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white text-[10px] tracking-[0.2em] uppercase transition-colors"
              data-testid={`launch-story-dl-${story.story_id}`}
              title="Descargar con texto sobreimpreso (lista para subir a IG)"
            >
              <Download className="w-3 h-3" /> Con texto
            </a>
          )}
          {ready && (
            <a
              href={dlRaw}
              download
              className="inline-flex items-center justify-center gap-1 h-8 px-2.5 rounded-full bg-white border border-[#E6E2DC] hover:border-[#A69076] text-[10px] tracking-[0.2em] uppercase text-[#1A1C1A] transition-colors"
              data-testid={`launch-story-dl-raw-${story.story_id}`}
              title="Descargar imagen sin texto (para agregar manualmente en IG)"
            >
              <ImageIcon className="w-3 h-3" />
            </a>
          )}
          <button
            onClick={() => onCopy(fullText)}
            className="inline-flex items-center justify-center h-8 px-2.5 rounded-full bg-white border border-[#E6E2DC] hover:border-[#A69076] text-[10px] tracking-[0.2em] uppercase text-[#1A1C1A]"
            title="Copiar texto de la historia"
            data-testid={`launch-story-copy-${story.story_id}`}
          >
            <Copy className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// UpcomingCard — "Próximas 24 h" quick preview at the top of the
// Cronograma tab. Polls /launch/upcoming every 60s and lists each
// piece with its kind, local time and title.
// ============================================================
function UpcomingCard({ onOpenModal }) {
  const [hours, setHours] = useState(24);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async (h = hours) => {
    setLoading(true);
    try {
      const r = await api.get(`/launch/upcoming?hours=${h}`);
      setItems(r.data.items || []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(hours);
    const t = setInterval(() => load(hours), 60000);
    return () => clearInterval(t);
  }, [hours]);

  const _fmt = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("es-MX", {
        weekday: "short", day: "2-digit", month: "short",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  const kindStyle = (k) => {
    if (k === "reel")     return { bg: "#FCEFE8", color: "#D97757", label: "REEL", icon: Film };
    if (k === "carousel") return { bg: "#1A1C1A", color: "#F4F0EB", label: "CARRUSEL", icon: Layers };
    return { bg: "#E6F0E8", color: "#5a7a64", label: "POST", icon: FileText };
  };

  return (
    <div
      className="bg-gradient-to-br from-[#1A1C1A] to-[#2a2b29] text-[#F4F0EB] rounded-2xl p-6 border border-[#1A1C1A]"
      data-testid="launch-upcoming-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#A69076] mb-1">
            <Clock className="w-3 h-3 inline mr-1.5 -mt-0.5" />
            Próximas {hours}h · publicaciones automáticas
          </div>
          <h3 className="font-display text-2xl">Lo que sale a continuación</h3>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {[24, 48, 72].map((h) => (
            <button
              key={h}
              type="button"
              onClick={() => setHours(h)}
              className={`text-[10px] tracking-[0.15em] uppercase px-3 py-1.5 rounded-full transition ${
                h === hours
                  ? "bg-[#D97757] text-white"
                  : "bg-[#2a2b29] text-[#A69076] hover:bg-[#3a3b39]"
              }`}
              data-testid={`upcoming-window-${h}`}
            >
              {h}h
            </button>
          ))}
          <button
            type="button"
            onClick={() => load(hours)}
            className="text-[10px] tracking-[0.15em] uppercase px-2.5 py-1.5 rounded-full bg-[#2a2b29] text-[#A69076] hover:bg-[#3a3b39] ml-1"
            title="Refrescar"
            data-testid="upcoming-refresh"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-[#A69076] italic py-6 text-center">
          {loading ? "Cargando…" : `No hay publicaciones programadas en las próximas ${hours}h.`}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const st = kindStyle(it.kind);
            const Icon = st.icon;
            return (
              <button
                key={`${it.kind}-${it.id}`}
                type="button"
                onClick={() => {
                  if (it.kind === "reel") return;  // Reel modal path differs — clicking day cell works.
                  // Build a minimal shape compatible with openPreview
                  const shape = {
                    id: it.id,
                    post_id_db: it.id,
                    day: it.launch_day,
                    topic: it.title,
                    caption: it.caption,
                    scheduled_for: it.scheduled_for,
                    status: it.status,
                    image_url: it.image_url,
                    slide_urls: it.image_url ? [it.image_url] : [],
                    service: "estetica",
                  };
                  onOpenModal(it.kind === "carousel" ? "carousel" : "post", shape);
                }}
                className="w-full flex items-center gap-3 bg-[#2a2b29] hover:bg-[#3a3b39] rounded-lg p-3 text-left transition"
                data-testid={`upcoming-item-${it.id}`}
              >
                <span
                  className="flex-shrink-0 inline-flex items-center gap-1.5 text-[9px] tracking-[0.15em] uppercase px-2.5 py-1 rounded-full font-medium"
                  style={{ background: st.bg, color: st.color }}
                >
                  <Icon className="w-3 h-3" />
                  {st.label}
                </span>
                <span className="text-[10px] tracking-[0.15em] uppercase text-[#A69076] flex-shrink-0 hidden sm:inline">
                  Día {it.launch_day}
                </span>
                <span className="text-sm text-[#F4F0EB] flex-1 truncate">
                  {it.title || "Sin título"}
                </span>
                <span className="text-[11px] text-[#A69076] flex-shrink-0 hidden md:inline">
                  {_fmt(it.scheduled_for)}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---- Compact thumbnail-first slot rendered inside each day cell ----
function CalendarSlotThumb({ slot, onOpen, dataTestId }) {
  const { kind, data } = slot;
  // Resolve preview image + top label per kind
  let thumb = null;
  let label = "";
  let title = "";
  let time = "";
  let badgeBg = "";
  let badgeText = "text-white";
  let Icon = null;
  const generated = !!(data.generated || data.image_base64 || data.image_url || data.video_url);

  if (kind === "reel") {
    label = "REEL"; time = "12:00"; badgeBg = "bg-[#D97757]"; Icon = Film;
    title = data.hook || data.topic || "";
    const r = data.reel || {};
    if (r.cover_url) thumb = `${BACKEND}${r.cover_url}`;
    else if (r.thumb_url) thumb = `${BACKEND}${r.thumb_url}`;
  } else if (kind === "post") {
    // Specific post type (QUOTE / TIP / MITO / DATO / PRODUCT / TESTIMONIO / etc.)
    label = (data.type || "POST").toUpperCase(); time = "18:00"; badgeBg = "bg-[#5a7a64]"; Icon = FileText;
    title = data.hook || data.topic || "";
    if (data.image_base64) thumb = data.image_base64.startsWith("data:") ? data.image_base64 : `data:image/jpeg;base64,${data.image_base64}`;
    else if (data.image_url) thumb = `${BACKEND}${data.image_url}`;
  } else if (kind === "carousel") {
    label = "CARRUSEL"; time = "20:00"; badgeBg = "bg-[#1A1C1A]"; Icon = Layers;
    title = data.hook || data.topic || "";
    const slides = data.slides || [];
    const first = slides[0] || {};
    if (first.image_base64) thumb = first.image_base64.startsWith("data:") ? first.image_base64 : `data:image/jpeg;base64,${first.image_base64}`;
    else if (first.image_url) thumb = `${BACKEND}${first.image_url}`;
  } else if (kind === "lifestyle") {
    label = data.is_suggestion ? "LIFESTYLE (SUG.)" : "LIFESTYLE";
    try {
      const dt = new Date(data.scheduled_for);
      const h = String(dt.getHours()).padStart(2, "0");
      const m = String(dt.getMinutes()).padStart(2, "0");
      time = `${h}:${m}`;
    } catch { time = "17:00"; }
    badgeBg = "bg-[#EF6C7D]";
    Icon = Heart;
    title = data.hook || data.topic || (data.caption || "").split("\n")[0] || "";
    if (data.image_base64) thumb = data.image_base64.startsWith("data:") ? data.image_base64 : `data:image/jpeg;base64,${data.image_base64}`;
    else if (data.image_url) thumb = `${BACKEND}${data.image_url}`;
  }

  const isSuggestion = data.is_suggestion;
  return (
    <button
      type="button"
      onClick={onOpen}
      title={title || label}
      data-testid={dataTestId}
      className={`group relative w-full bg-white border ${isSuggestion ? "border-dashed border-[#EF6C7D]/70" : "border-[#E6E2DC]"} hover:border-[#A69076] rounded-md overflow-hidden transition-all hover:shadow-md flex items-stretch text-left`}
    >
      {/* Thumbnail column */}
      <div className="relative w-14 h-14 flex-shrink-0 bg-[#F4F0EB]">
        {thumb ? (
          <img src={thumb} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#A69076]">
            {Icon && <Icon className="w-4 h-4" />}
          </div>
        )}
        {generated && kind !== "lifestyle" && (
          <span className="absolute bottom-0.5 right-0.5 bg-white/90 rounded-full p-0.5">
            <CheckCircle2 className="w-2.5 h-2.5 text-[#0a7f28]" />
          </span>
        )}
      </div>
      {/* Info column */}
      <div className="flex-1 min-w-0 px-1.5 py-1 flex flex-col justify-between">
        <div className="flex items-center gap-1 flex-wrap">
          <span className={`inline-flex items-center gap-0.5 ${badgeBg} ${badgeText} text-[7px] tracking-[0.12em] uppercase font-bold px-1 py-0.5 rounded`}>
            {Icon && <Icon className="w-2 h-2" />}
            {label}
          </span>
          <span className="text-[8px] text-[#5C5E5C] tracking-wider">{time}</span>
        </div>
        <p className="text-[9px] text-[#1A1C1A] leading-[1.15] line-clamp-2 mt-0.5">
          {title || <span className="italic text-[#B0A99E]">Sin título aún</span>}
        </p>
      </div>
      {data.fb_missing && (
        <span
          className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-[#E53E3E] border border-white"
          title="Sin cross-post en Facebook"
        />
      )}
    </button>
  );
}


